import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  IPrintTemplate,
  ITemplateElement,
  ElementType,
  TextAlign,
  IAvailableFieldOption,
  fieldGroups,
  ITableConfig
} from '../../types/printTemplate';
// Removed GlobalDragFix import as we're using the global JavaScript solution
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ListSubheader,
  SelectChangeEvent,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Collapse,
  Chip
} from '@mui/material';
import {
  Search,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';
import {
  Save as SaveIcon,
  ArrowBack as BackIcon,
  Preview as PreviewIcon,
  TextFormat as TextIcon,
  Image as ImageIcon,
  QrCode as BarcodeIcon,
  TableChart as TableIcon,
  Square as ShapeIcon,
  TextFields as FieldIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  Info as InfoIcon,
  DeleteOutline as DeleteOutlineIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import * as printTemplateService from '../../services/printTemplateService';

const PrintTemplateEditorPage = (): React.ReactElement | null => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  
  // State declarations
  const [template, setTemplate] = useState<IPrintTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [selectedElementId, setSelectedElementId] = useState<string | null>(null);
  
  // Undo/Redo stacks
  const [undoStack, setUndoStack] = useState<IPrintTemplate[]>([]);
  const [redoStack, setRedoStack] = useState<IPrintTemplate[]>([]);
  
  // حالة جديدة لتتبع الحقول المختارة
  const [selectedFields, setSelectedFields] = useState<IAvailableFieldOption[]>([]);
  
  // دالة مساعدة لتنظيف قائمة الحقول المختارة
  const cleanupSelectedFields = (currentTemplate: IPrintTemplate) => {
    // الحصول على جميع الحقول المستخدمة في التصميم
    const usedFields: string[] = [];
    
    currentTemplate.elements.forEach(element => {
      if (element.elementType === 'field' && element.sourceField) {
        usedFields.push(element.sourceField);
      } else if (element.elementType === 'barcode' && element.sourceField) {
        usedFields.push(element.sourceField);
      } else if (element.elementType === 'image') {
        usedFields.push(`image_${element.id}`);
      } else if (element.elementType === 'shape') {
        usedFields.push(`shape_${element.id}`);
      } else if (element.elementType === 'barcode' && !element.sourceField) {
        usedFields.push(`barcode_${element.id}`);
      } else if (element.elementType === 'table') {
        usedFields.push(`table_${element.id}`);
      }
    });
    
    // تصفية قائمة الحقول المختارة للاحتفاظ بالحقول المستخدمة فقط
    setSelectedFields(prevFields => {
      const cleanedFields = prevFields.filter(field => {
        return usedFields.includes(field.field);
      });
      
      if (cleanedFields.length !== prevFields.length ||
          !cleanedFields.every((field, index) => prevFields[index]?.field === field.field)) {
        console.log('تنظيف فوري لقائمة الحقول المختارة:', {
          قبل: prevFields.map(f => f.field),
          بعد: cleanedFields.map(f => f.field),
          المستخدمة: usedFields
        });
        return cleanedFields;
      }
      return prevFields;
    });
  };
  
  // Add to undo stack and clear redo stack
  const addToUndoStack = (template: IPrintTemplate) => {
    setUndoStack(prev => [...prev, JSON.parse(JSON.stringify(template))]);
    setRedoStack([]);
  };
  
  // New state declarations
  const [draggedElementId, setDraggedElementId] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizing, setResizing] = useState<boolean>(false);
  const [resizeDirection, setResizeDirection] = useState<string | null>(null);
  const [showGrid, setShowGrid] = useState<boolean>(true);
  const [snapToGrid, setSnapToGrid] = useState<boolean>(true);
  const [gridSize, setGridSize] = useState<number>(10);
  const [showGuides, setShowGuides] = useState<boolean>(true);
  const [activeGuides, setActiveGuides] = useState<Array<{ type: string; position: number }>>([]);
  
  // معامل التحويل من ملليمتر إلى بكسل - قابل للتعديل
  const [mmToPxRatio, setMmToPxRatio] = useState<number>(2.83);
  const [mmToPxRatioInput, setMmToPxRatioInput] = useState<string>('2.83');
  
  // للاحتفاظ بمرجع منطقة التصميم
  const designAreaRef = useRef<HTMLDivElement | null>(null);
  
  // لمعرفة ما إذا كان المؤشر فوق مقبض التحجيم لتغيير شكل المؤشر
  const [resizeHover, setResizeHover] = useState<string | null>(null);
  
  // قائمة الحقول المتاحة للاختيار منها
  const availableFields: IAvailableFieldOption[] = [
    // معلومات الفرع
    { field: 'branchInfo.companyName', label: 'اسم الشركة', description: 'اسم الشركة المسجل رسمياً', group: fieldGroups.BRANCH_INFO },
    { field: 'branchInfo.branchName', label: 'اسم الفرع', description: 'اسم الفرع الحالي', group: fieldGroups.BRANCH_INFO },
    { field: 'branchInfo.commercialRegister', label: 'السجل التجاري', description: 'رقم السجل التجاري للشركة', group: fieldGroups.BRANCH_INFO },
    { field: 'branchInfo.taxNumber', label: 'الرقم الضريبي', description: 'رقم التسجيل الضريبي', group: fieldGroups.BRANCH_INFO },
    { field: 'branchInfo.branchAddress', label: 'عنوان الفرع', description: 'العنوان التفصيلي للفرع', group: fieldGroups.BRANCH_INFO },
    { field: 'branchInfo.phoneNumber', label: 'رقم الهاتف', description: 'رقم الهاتف الرئيسي للفرع', group: fieldGroups.BRANCH_INFO },
    
    // معلومات الفاتورة
    { field: 'invoice.number', label: 'رقم الفاتورة', description: 'الرقم التسلسلي للفاتورة', group: fieldGroups.INVOICE_INFO },
    { field: 'invoice.date', label: 'تاريخ الفاتورة', description: 'تاريخ إصدار الفاتورة', group: fieldGroups.INVOICE_INFO },
    { field: 'invoice.dueDate', label: 'تاريخ الاستحقاق', description: 'الموعد النهائي للدفع', group: fieldGroups.INVOICE_INFO },
    { field: 'invoice.paymentMethod', label: 'طريقة الدفع', description: 'الطريقة المستخدمة لدفع قيمة الفاتورة', group: fieldGroups.INVOICE_INFO },
    { field: 'invoice.status', label: 'حالة الفاتورة', description: 'الحالة الحالية للفاتورة (مدفوعة، معلقة، إلخ)', group: fieldGroups.INVOICE_INFO },
    { field: 'invoice.reference', label: 'المرجع', description: 'رقم مرجعي اختياري للفاتورة', group: fieldGroups.INVOICE_INFO },
    { field: 'invoice.notes', label: 'الملاحظات', description: 'ملاحظات إضافية على الفاتورة', group: fieldGroups.INVOICE_INFO },
    
    // معلومات العميل
    { field: 'customer.name', label: 'اسم العميل', description: 'الاسم الكامل للعميل', group: fieldGroups.CUSTOMER_INFO },
    { field: 'customer.phone', label: 'رقم الهاتف', description: 'رقم هاتف العميل', group: fieldGroups.CUSTOMER_INFO },
    { field: 'customer.address', label: 'العنوان', description: 'عنوان العميل', group: fieldGroups.CUSTOMER_INFO },
    { field: 'customer.vatNumber', label: 'الرقم الضريبي', description: 'الرقم الضريبي للعميل (إن وجد)', group: fieldGroups.CUSTOMER_INFO },
    { field: 'customer.accountBalance', label: 'رصيد الحساب', description: 'رصيد حساب العميل الحالي', group: fieldGroups.CUSTOMER_INFO },
    
    // معلومات المورد
    { field: 'supplier.name', label: 'اسم المورد', description: 'اسم المورد الكامل', group: fieldGroups.SUPPLIER_INFO },
    { field: 'supplier.phone', label: 'رقم الهاتف', description: 'رقم هاتف المورد', group: fieldGroups.SUPPLIER_INFO },
    { field: 'supplier.address', label: 'العنوان', description: 'عنوان المورد', group: fieldGroups.SUPPLIER_INFO },
    { field: 'supplier.vatNumber', label: 'الرقم الضريبي', description: 'الرقم الضريبي للمورد', group: fieldGroups.SUPPLIER_INFO },
    { field: 'supplier.accountBalance', label: 'رصيد الحساب', description: 'رصيد حساب المورد الحالي', group: fieldGroups.SUPPLIER_INFO },
    
    // المجاميع
    { field: 'totals.subtotal', label: 'المجموع الفرعي', description: 'مجموع قيمة الأصناف قبل الضريبة والخصم', group: fieldGroups.TOTALS },
    { field: 'totals.discount', label: 'الخصم', description: 'قيمة الخصم الإجمالي', group: fieldGroups.TOTALS },
    { field: 'totals.tax', label: 'ضريبة القيمة المضافة', description: 'مبلغ ضريبة القيمة المضافة', group: fieldGroups.TOTALS },
    { field: 'totals.shipping', label: 'تكلفة الشحن', description: 'تكلفة شحن البضائع', group: fieldGroups.TOTALS },
    { field: 'totals.total', label: 'المجموع النهائي', description: 'المبلغ الإجمالي النهائي للفاتورة', group: fieldGroups.TOTALS },
    { field: 'totals.amountPaid', label: 'المبلغ المدفوع', description: 'المبلغ المدفوع من قيمة الفاتورة', group: fieldGroups.TOTALS },
    { field: 'totals.balance', label: 'المبلغ المتبقي', description: 'المبلغ المتبقي من قيمة الفاتورة', group: fieldGroups.TOTALS },
    { field: 'totals.totalInWords', label: 'المجموع بالكلمات', description: 'المبلغ الإجمالي مكتوباً بالحروف', group: fieldGroups.TOTALS },
    
    // حقول جدول المنتجات (تستخدم في عنصر الجدول)
    { field: 'product', label: 'المنتج (مجمع)', description: 'اسم المنتج وخصائص المتغير في عمود واحد', group: fieldGroups.PRODUCT_TABLE },
    { field: 'product.barcode', label: 'الباركود', description: 'الرمز الشريطي للمنتج', group: fieldGroups.PRODUCT_TABLE },
    { field: 'product.name', label: 'اسم المنتج', description: 'اسم المنتج أو الخدمة', group: fieldGroups.PRODUCT_TABLE },
    { field: 'product.unit', label: 'الوحدة', description: 'وحدة قياس المنتج', group: fieldGroups.PRODUCT_TABLE },
    { field: 'product.quantity', label: 'الكمية', description: 'كمية المنتج', group: fieldGroups.PRODUCT_TABLE },
    { field: 'product.price', label: 'السعر', description: 'سعر الوحدة', group: fieldGroups.PRODUCT_TABLE },
    { field: 'product.priceWithTax', label: 'السعر شامل الضريبة', description: 'سعر الوحدة شامل الضريبة', group: fieldGroups.PRODUCT_TABLE },
    { field: 'product.priceBeforeTax', label: 'قبل الضريبة', description: 'سعر الوحدة قبل الضريبة', group: fieldGroups.PRODUCT_TABLE },
    { field: 'product.taxPercentage', label: 'الضريبة %', description: 'نسبة الضريبة المطبقة على المنتج', group: fieldGroups.PRODUCT_TABLE },
    { field: 'product.taxValue', label: 'قيمة الضريبة', description: 'قيمة الضريبة المطبقة على المنتج', group: fieldGroups.PRODUCT_TABLE },
    { field: 'product.discount', label: 'الخصم', description: 'قيمة الخصم على المنتج', group: fieldGroups.PRODUCT_TABLE },
    { field: 'product.tax', label: 'الضريبة', description: 'قيمة الضريبة المطبقة على المنتج', group: fieldGroups.PRODUCT_TABLE },
    { field: 'product.total', label: 'الإجمالي', description: 'إجمالي سعر المنتج', group: fieldGroups.PRODUCT_TABLE },
    
    // أخرى
    { field: 'misc.date', label: 'التاريخ الحالي', description: 'تاريخ الطباعة الحالي', group: fieldGroups.OTHER },
    { field: 'misc.time', label: 'الوقت الحالي', description: 'وقت الطباعة الحالي', group: fieldGroups.OTHER },
    { field: 'misc.pageNumber', label: 'رقم الصفحة', description: 'رقم الصفحة الحالية', group: fieldGroups.OTHER },
    { field: 'misc.qrCode', label: 'QR رمز الفاتورة الضريبية', description: 'رمز QR يحتوي على معلومات الفاتورة الضريبية', group: fieldGroups.OTHER }
  ];
  
  // جلب تفاصيل القالب عند تحميل الصفحة
  useEffect(() => {
    const fetchTemplate = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const data = await printTemplateService.getPrintTemplateById(id);
        setTemplate(data);
        
        // استرجاع الحقول المختارة إذا كانت محفوظة مع القالب
        if (data.selectedFields && Array.isArray(data.selectedFields)) {
          setSelectedFields(data.selectedFields);
          console.log('تم استرجاع الحقول المختارة:', data.selectedFields);
        }
        
        setError(null);
      } catch (err) {
        console.error('خطأ في جلب تفاصيل القالب:', err);
        setError('حدث خطأ أثناء تحميل بيانات القالب. يرجى المحاولة مرة أخرى.');
      } finally {
        setLoading(false);
      }
    };

    fetchTemplate();
  }, [id]);

  // حفظ التغييرات
  const handleSaveTemplate = async () => {
    if (!template || !id) return;
    
    try {
      // تأكد من وجود إعدادات الجدول بشكل صحيح لكل الجداول
      const elementsWithCorrectTableConfig = template.elements.map(element => {
        if (element.elementType === 'table' && element.tableConfig) {
          // التأكد من وجود خصائص الجدول المضغوط وحجم الخط
          const updatedTableConfig = {
            ...element.tableConfig,
            compactView: element.tableConfig.compactView !== undefined ? element.tableConfig.compactView : false,
            fontSize: element.tableConfig.fontSize !== undefined ? element.tableConfig.fontSize : 9,
            headerFontSize: element.tableConfig.headerFontSize !== undefined ? element.tableConfig.headerFontSize : 10
          };
          
          return {
            ...element,
            tableConfig: updatedTableConfig
          };
        }
        return element;
      });
      
      const updatedTemplate = {
        ...template,
        elements: elementsWithCorrectTableConfig,
        selectedFields: selectedFields // إضافة الحقول المختارة
      };
      
      // طباعة قيم الجداول للتأكد من أنها موجودة
      updatedTemplate.elements.forEach(element => {
        if (element.elementType === 'table' && element.tableConfig) {
          console.log(`جدول (${element.id}):`, {
            compactView: element.tableConfig.compactView,
            fontSize: element.tableConfig.fontSize,
            headerFontSize: element.tableConfig.headerFontSize
          });
        }
      });
      
      console.log('حفظ الحقول المختارة:', selectedFields);
      
      // تحديث الحالة المحلية
      setTemplate(updatedTemplate);
      
      // إرسال التحديث إلى الخادم
      await printTemplateService.updatePrintTemplate(id, {
        elements: elementsWithCorrectTableConfig,
        selectedFields: selectedFields // حفظ الحقول المختارة
      });
      
      setIsDirty(false);
      // تحديث لقطة للتصميم الحالي
      await printTemplateService.createTemplateSnapshot(id, `لقطة تلقائية - ${new Date().toLocaleString('ar')}`);
      
      // إضافة النسخة الحالية إلى مكدس التراجع وإعادة تعيين مكدس الإعادة
      addToUndoStack(updatedTemplate);
      
      alert('تم حفظ التصميم بنجاح');
    } catch (err) {
      console.error('خطأ في حفظ القالب:', err);
      setError('حدث خطأ أثناء حفظ التغييرات. يرجى المحاولة مرة أخرى.');
    }
  };

  // العودة إلى صفحة إعدادات القوالب
  const handleGoBack = () => {
    if (isDirty) {
      const confirm = window.confirm('لديك تغييرات غير محفوظة. هل ترغب في المتابعة؟');
      if (!confirm) return;
    }
    
    navigate('/invoices/print-templates');
  };

  // معاينة قالب الطباعة
  const handlePreviewTemplate = () => {
    if (!template) return;
    
    // فتح نافذة جديدة وعرض القالب
    const previewWindow = window.open('', '_blank');
    if (!previewWindow) {
      alert('الرجاء السماح بالنوافذ المنبثقة لعرض المعاينة');
      return;
    }
    
    // إنشاء عناصر HTML من عناصر القالب
    let htmlContent = `
      <!DOCTYPE html>
      <html dir="rtl" lang="ar">
      <head>
        <meta charset="UTF-8">
        <meta http-equiv="Content-Type" content="text/html; charset=UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>معاينة ${template.name}</title>
        <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;700&display=swap">
        <style>
          @font-face {
            font-family: 'DroidArabicNaskh';
            src: url('https://cdn.jsdelivr.net/npm/droid-arabic-naskh@1.0.0/DroidNaskh-Regular.ttf') format('truetype');
            font-weight: normal;
          }
          @font-face {
            font-family: 'DroidArabicNaskh';
            src: url('https://cdn.jsdelivr.net/npm/droid-arabic-naskh@1.0.0/DroidNaskh-Bold.ttf') format('truetype');
            font-weight: bold;
          }
          body {
            font-family: 'Cairo', 'DroidArabicNaskh', 'Arial', sans-serif;
            margin: 0;
            padding: 20px;
          }
          .page {
            width: ${template.orientation === 'landscape' ?
                    (template.paperSize === 'A4' ? '297mm' :
                    template.paperSize === 'A5' ? '210mm' :
                    template.paperSize === 'A6' ? '148mm' :
                    template.paperSize === 'thermal80mm' ? '80mm' :
                    template.paperSize === 'thermal58mm' ? '58mm' :
                    `${template.customHeight || 297}mm`) :
                    // أبعاد عمودية عادية
                    (template.paperSize === 'A4' ? '210mm' :
                    template.paperSize === 'A5' ? '148mm' :
                    template.paperSize === 'A6' ? '105mm' :
                    template.paperSize === 'thermal80mm' ? '80mm' :
                    template.paperSize === 'thermal58mm' ? '58mm' :
                    `${template.customWidth || 210}mm`)};
            height: ${template.orientation === 'landscape' ?
                    (template.paperSize === 'A4' ? '210mm' :
                    template.paperSize === 'A5' ? '148mm' :
                    template.paperSize === 'A6' ? '105mm' :
                    template.paperSize === 'thermal80mm' ? '297mm' :
                    template.paperSize === 'thermal58mm' ? '297mm' :
                    `${template.customWidth || 210}mm`) :
                    // أبعاد عمودية عادية
                    (template.paperSize === 'A4' ? '297mm' :
                    template.paperSize === 'A5' ? '210mm' :
                    template.paperSize === 'A6' ? '148mm' :
                    template.paperSize === 'thermal80mm' ? '297mm' :
                    template.paperSize === 'thermal58mm' ? '297mm' :
                    `${template.customHeight || 297}mm`)};
            position: relative;
            background-color: white;
            box-shadow: 0 0 10px rgba(0,0,0,0.1);
            margin: 0 auto;
            padding: 10mm;
            box-sizing: border-box;
          }
          .element {
            position: absolute;
            box-sizing: border-box;
            font-family: inherit;
          }
          .template-image {
            max-width: 100%;
            max-height: 100%;
            object-fit: contain;
          }
          @media print {
            body {
              padding: 0;
            }
            .page {
              box-shadow: none;
            }
            .no-print {
              display: none;
            }
            /* تحسين مظهر جميع الصور في الطباعة */
            img {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
              max-width: 100%;
              max-height: 100%;
              display: block !important;
              visibility: visible !important;
            }
            /* تحسين مظهر الـ QR في الطباعة */
            .zatca-qr-container {
              background-color: white !important;
              padding: 5px !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            .zatca-qr-container img {
              background-color: white !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
              display: block !important;
            }
          }
        </style>
      </head>
      <body>
        <div class="no-print" style="text-align: center; margin-bottom: 20px;">
          <button onclick="window.print()">طباعة</button>
          <button onclick="window.close()">إغلاق</button>
        </div>
        <div class="page">
    `;
    
    // إضافة العناصر
    template.elements?.forEach(element => {
      let elementHTML = `
        <div class="element" style="
          top: ${element.position.y}px;
          left: ${element.position.x}px;
          width: ${element.style.width}px;
          height: ${element.style.height}px;
          padding: ${element.style.padding}px;
          color: ${element.style.color};
          background-color: ${element.style.backgroundColor};
          ${element.style.borderWidth ? `border: ${element.style.borderWidth}px ${element.style.borderStyle} ${element.style.borderColor};` : ''}
          text-align: ${element.style.textAlign};
          font-size: ${element.style.fontSize}px;
          font-family: ${element.style.fontFamily === 'Arial' ? "'Cairo', 'DroidArabicNaskh', 'Arial'" : element.style.fontFamily}, sans-serif;
          font-weight: ${element.style.fontWeight};
          z-index: ${element.position.zIndex};
        ">`;
        
      if (element.elementType === 'text') {
        elementHTML += element.content;
      } else if (element.elementType === 'field') {
        // استخدام الاسم المخصص للحقل إذا كان موجودًا وفعالًا، وإلا استخدام الاسم الافتراضي
        let fieldLabel = element.sourceField || 'غير محدد';
        const fieldOption = availableFields.find(f => f.field === element.sourceField);
        if (element.showFieldName) {
          // استخدام حقل المحتوى كاسم مخصص للحقل
          const displayName = element.content || (fieldOption ? fieldOption.label : fieldLabel);
          elementHTML += `${displayName}: [${fieldLabel}]`;
        } else {
          elementHTML += `[${fieldLabel}]`;
        }
      } else if (element.elementType === 'image') {
        elementHTML += element.content ?
          `<img src="${element.content}" class="template-image" style="max-width: 100%; max-height: 100%; object-fit: contain; display: block;" />` :
          '<div style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; border: 1px dashed #ccc; color: #999;">(صورة)</div>';
      } else if (element.elementType === 'barcode') {
        if (element.barcodeConfig?.isZATCAQR) {
          // إنشاء باركود QR للفاتورة الإلكترونية ZATCA
          elementHTML += `
            <div class="zatca-qr-container" style="width: 100%; height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center;">
              <div class="zatca-qr-code" data-qr-type="zatca"
                data-seller-name="{{branchInfo.companyName}}"
                data-tax-number="{{branchInfo.taxNumber}}"
                data-invoice-date="{{invoice.date}}"
                data-invoice-total="{{totals.total}}"
                data-tax-amount="{{totals.tax}}">
              </div>
              <div style="margin-top: 5px; font-size: 10px;">رمز QR للفاتورة الإلكترونية</div>
            </div>`;
        } else {
          elementHTML += element.content ? `<div>[باركود: ${element.content}]</div>` : '(باركود)';
        }
      } else if (element.elementType === 'table') {
        elementHTML += '<table style="width: 100%; border-collapse: collapse;">';
        if (element.tableConfig?.showHeaders) {
          elementHTML += '<thead><tr>';
          element.tableConfig.columns.forEach(col => {
            const headerFontSize = element.tableConfig?.headerFontSize || element.tableConfig?.fontSize || 10;
            elementHTML += `<th style="border: 1px solid #ddd; padding: 8px; background-color: ${element.tableConfig?.styles.headerBackground}; color: ${element.tableConfig?.styles.headerTextColor}; font-size: ${headerFontSize}px;">${col.label}</th>`;
          });
          elementHTML += '</tr></thead>';
        }
        elementHTML += '<tbody>';
        // نضيف نموذج بيانات للعرض
        const sampleItems = [
          { product: 'منتج نموذجي 1', productName: 'منتج 1', quantity: 5, price: 100, priceWithTax: 115, taxPercentage: 15, taxValue: 15, total: 500 },
          { product: 'منتج نموذجي 2', productName: 'منتج 2', quantity: 3, price: 75, priceWithTax: 86.25, taxPercentage: 15, taxValue: 11.25, total: 225 }
        ];
        
        // عدد الصفوف المعروضة في المعاينة - سنعرض صفين فقط للنموذج
        const rowCount = element.tableConfig?.dynamicHeight ? 2 : 2;
        
        for (let i = 0; i < rowCount; i++) {
          const item = sampleItems[i % sampleItems.length];
          elementHTML += `<tr style="background-color: ${i % 2 === 0 ? element.tableConfig?.styles.rowBackground : element.tableConfig?.styles.alternateRowBackground};">`;
          element.tableConfig?.columns.forEach(col => {
            // استخدام بيانات العينة للعرض بدلاً من القيم العامة
            const value = item[col.field] || `[${col.field}]`;
            elementHTML += `<td style="border: 1px solid #ddd; padding: 8px; color: ${element.tableConfig?.styles.rowTextColor};">${value}</td>`;
          });
          elementHTML += '</tr>';
        }
        
        // إضافة ملاحظة عن الجدول الديناميكي
        if (element.tableConfig?.dynamicHeight) {
          elementHTML += `<tr><td colspan="${element.tableConfig.columns.filter(c => c.visible !== false).length}" style="text-align: center; font-style: italic; padding: 5px; color: #666; font-size: 11px; border: none;">* سيتم تمديد الجدول تلقائياً حسب عدد المنتجات في الفاتورة *</td></tr>`;
        }
        elementHTML += '</tbody></table>';
      } else if (element.elementType === 'shape') {
        if (element.content === 'rectangle') {
          // لا نحتاج لشيء إضافي، الحدود تكفي
        } else if (element.content === 'circle') {
          elementHTML = `<div class="element" style="
            top: ${element.position.y}px;
            left: ${element.position.x}px;
            width: ${element.style.width}px;
            height: ${element.style.height}px;
            background-color: ${element.style.backgroundColor};
            border-radius: 50%;
            border: ${element.style.borderWidth}px ${element.style.borderStyle} ${element.style.borderColor};
            z-index: ${element.position.zIndex};
          "></div>`;
        } else if (element.content === 'line') {
          const angle = element.position.rotation || 0;
          elementHTML = `<div class="element" style="
            top: ${element.position.y}px;
            left: ${element.position.x}px;
            width: ${element.style.width}px;
            height: ${element.style.borderWidth || 1}px;
            background-color: ${element.style.borderColor};
            transform: rotate(${angle}deg);
            transform-origin: left;
            z-index: ${element.position.zIndex};
          "></div>`;
        }
      }
      
      elementHTML += `</div>`;
      htmlContent += elementHTML;
    });
    // إضافة سكريبت JavaScript لإنشاء باركود QR للفاتورة الإلكترونية وإدارة الجدول الديناميكي
    htmlContent += `
        </div>
        <script>
          // معلومات عناصر القالب للاستخدام في إعادة الترتيب
          const templateElements = ${JSON.stringify(template.elements)};
          
          // تعريف العناصر الديناميكية
          const dynamicTables = templateElements.filter(element =>
            element.elementType === 'table' &&
            element.tableConfig &&
            element.tableConfig.dynamicHeight === true
          );

          // محاكاة بيانات الفاتورة للعرض
          const sampleInvoiceItems = [
            { product: 'منتج 1', quantity: 5, price: 100, total: 500 },
            { product: 'منتج 2', quantity: 2, price: 75, total: 150 },
            { product: 'منتج 3', quantity: 3, price: 200, total: 600 },
            { product: 'منتج 4', quantity: 1, price: 50, total: 50 },
            { product: 'منتج 5', quantity: 4, price: 125, total: 500 }
          ];
          
          // وظيفة تنفذ بعد تحميل الصفحة لتطبيق الارتفاعات الديناميكية وإزاحة العناصر
          function handleDynamicTables() {
            // لكل جدول ديناميكي
            dynamicTables.forEach(tableElement => {
              // العثور على عنصر الجدول
              const tableEl = document.getElementById(\`element-\${tableElement.id}\`);
              if (!tableEl) return;
              
              // حفظ الارتفاع الأصلي للجدول
              const originalHeight = tableElement.style.height;
              
              // إنشاء محتوى الجدول باستخدام عينة بيانات الفاتورة
              let tableHTML = '<table style="width: 100%; border-collapse: collapse;">';
              
              // إضافة رؤوس الأعمدة إذا كانت مطلوبة
              if (tableElement.tableConfig?.showHeaders) {
                tableHTML += '<thead><tr>';
                tableElement.tableConfig.columns
                  .filter(col => col.visible !== false)
                  .forEach(col => {
                    const headerFontSize = tableElement.tableConfig?.headerFontSize || tableElement.tableConfig?.fontSize || 10;
                    tableHTML += \`<th style="border: 1px solid #ddd; padding: 8px; background-color: \${tableElement.tableConfig?.styles.headerBackground}; color: \${tableElement.tableConfig?.styles.headerTextColor}; font-size: \${headerFontSize}px;">\${col.label}</th>\`;
                  });
                tableHTML += '</tr></thead>';
              }
              
              // إضافة صفوف البيانات
              tableHTML += '<tbody>';
              
              // استخدام بيانات عينة للمحاكاة
              sampleInvoiceItems.forEach((item, index) => {
                const rowStyle = index % 2 === 0
                  ? tableElement.tableConfig?.styles.rowBackground
                  : tableElement.tableConfig?.styles.alternateRowBackground;
                
                tableHTML += \`<tr style="background-color: \${rowStyle};">\`;
                
                tableElement.tableConfig?.columns
                  .filter(col => col.visible !== false)
                  .forEach(col => {
                    // استخدام بيانات العينة حسب اسم الحقل
                    let cellValue = item[col.field] || \`[\${col.field}]\`;
                    tableHTML += \`<td style="border: 1px solid #ddd; padding: 8px; color: \${tableElement.tableConfig?.styles.rowTextColor};">\${cellValue}</td>\`;
                  });
                
                tableHTML += '</tr>';
              });
              
              tableHTML += '</tbody></table>';
              
              // تعيين محتوى الجدول
              tableEl.innerHTML = tableHTML;
              
              // حساب الارتفاع الجديد المطلوب للجدول
              const tableHeight = tableEl.querySelector('table').offsetHeight;
              
              // تعديل ارتفاع الجدول
              tableEl.style.height = \`\${tableHeight + 20}px\`; // إضافة هامش إضافي
              
              // إذا كانت خاصية إزاحة العناصر مفعّلة
              if (tableElement.tableConfig?.pushElementsDown) {
                // حساب مقدار الإزاحة (الفرق بين الارتفاع الجديد والأصلي)
                const heightDifference = tableHeight - originalHeight;
                
                // لا داعي للإزاحة إذا كان الارتفاع الجديد أقل من أو يساوي الأصلي
                if (heightDifference <= 0) return;
                
                // إزاحة جميع العناصر التي تقع تحت الجدول
                templateElements.forEach(otherElement => {
                  // تجاهل العنصر نفسه
                  if (otherElement.id === tableElement.id) return;
                  
                  // إذا كان العنصر الآخر يقع تحت الجدول
                  if (otherElement.position.y > tableElement.position.y + originalHeight) {
                    const otherEl = document.getElementById(\`element-\${otherElement.id}\`);
                    if (otherEl) {
                      // حساب الموضع الرأسي الجديد بإضافة مقدار الإزاحة
                      const newY = otherElement.position.y + heightDifference;
                      otherEl.style.top = \`\${newY}px\`;
                    }
                  }
                });
              }
            });
          }
          
          // تنفيذ الوظيفة بعد تحميل الصفحة
          document.addEventListener('DOMContentLoaded', function() {
            setTimeout(handleDynamicTables, 100); // تأخير قصير للتأكد من تحميل جميع العناصر
          });
          // دالة لتكوين TLV (Tag-Length-Value) لبيانات الفاتورة الإلكترونية وفقاً لمتطلبات هيئة الزكاة والضريبة والجمارك
          function createTLVBase64(sellerName, taxNumber, invoiceDate, invoiceTotal, taxAmount) {
            // وظيفة مساعدة لتكوين TLV
            function toTLV(tag, value) {
              const textEncoder = new TextEncoder();
              const tagByte = new Uint8Array([tag]);
              const valueBytes = textEncoder.encode(value);
              const lengthByte = new Uint8Array([valueBytes.length]);
              
              // دمج المصفوفات معًا
              const result = new Uint8Array(tagByte.length + lengthByte.length + valueBytes.length);
              result.set(tagByte, 0);
              result.set(lengthByte, tagByte.length);
              result.set(valueBytes, tagByte.length + lengthByte.length);
              
              return result;
            }

            // تنسيق البيانات للتوافق مع متطلبات ZATCA
            // تحويل التاريخ إلى صيغة ISO 8601 إذا لم يكن كذلك بالفعل
            let formattedDate = invoiceDate;
            try {
              // إذا كان التاريخ بصيغة نصية غير ISO، نحاول تحويله
              if (!invoiceDate.includes('T') && !invoiceDate.includes('Z') && !invoiceDate.includes('+')) {
                const date = new Date(invoiceDate);
                formattedDate = date.toISOString();
              }
            } catch (e) {
              console.warn('خطأ في تنسيق التاريخ:', e);
            }

            // إنشاء TLV لكل حقل وفقًا لمتطلبات ZATCA
            const tlv1 = toTLV(1, sellerName);
            const tlv2 = toTLV(2, taxNumber);
            const tlv3 = toTLV(3, formattedDate);
            const tlv4 = toTLV(4, invoiceTotal);
            const tlv5 = toTLV(5, taxAmount);
            
            // دمج جميع TLVs معًا
            const combinedLength = tlv1.length + tlv2.length + tlv3.length + tlv4.length + tlv5.length;
            const tlvBuffer = new Uint8Array(combinedLength);
            let offset = 0;
            
            tlvBuffer.set(tlv1, offset);
            offset += tlv1.length;
            tlvBuffer.set(tlv2, offset);
            offset += tlv2.length;
            tlvBuffer.set(tlv3, offset);
            offset += tlv3.length;
            tlvBuffer.set(tlv4, offset);
            offset += tlv4.length;
            tlvBuffer.set(tlv5, offset);
            
            // تحويل TLV إلى Base64
            return btoa(String.fromCharCode.apply(null, Array.from(tlvBuffer)));
          }

          // استدعاء مكتبة QRCode لإنشاء رمز QR
          function loadScript(url, callback) {
            const script = document.createElement('script');
            script.type = 'text/javascript';
            script.src = url;
            script.onload = callback;
            script.onerror = function() {
              console.error('فشل تحميل السكريبت:', url);
              // محاولة تحميل مكتبة QR code البديلة
              const fallbackUrl = 'https://cdn.jsdelivr.net/npm/qrcode-generator@1.4.4/qrcode.min.js';
              if (url !== fallbackUrl) {
                loadScript(fallbackUrl, callback);
              } else {
                callback(); // الاستمرار على أي حال
              }
            };
            document.head.appendChild(script);
          }

          // تحميل مكتبة QRCode.js ثم إنشاء رموز QR
          // تحميل مكتبة QR code الحديثة
          loadScript('https://cdn.jsdelivr.net/npm/qrcode@1.5.1/build/qrcode.min.js', function() {
            // معالجة رموز QR للفاتورة الإلكترونية ZATCA
            document.querySelectorAll('[data-qr-type="zatca"]').forEach(function(element) {
              try {
                const sellerName = element.getAttribute('data-seller-name').replace(/{{|}}/g, '');
                const taxNumber = element.getAttribute('data-tax-number').replace(/{{|}}/g, '');
                const invoiceDate = element.getAttribute('data-invoice-date').replace(/{{|}}/g, '');
                const invoiceTotal = element.getAttribute('data-invoice-total').replace(/{{|}}/g, '');
                const taxAmount = element.getAttribute('data-tax-amount').replace(/{{|}}/g, '');
                
                // بيانات للمعاينة
                const previewData = {
                  'branchInfo.companyName': 'مؤسسة التقنية الذكية',
                  'branchInfo.taxNumber': '123456789012345',
                  'invoice.date': '${new Date().toISOString()}',
                  'totals.total': '115.00',
                  'totals.tax': '15.00'
                };
                
                // استخدام البيانات الفعلية أو بيانات المعاينة
                const actualSellerName = previewData[sellerName] || sellerName;
                const actualTaxNumber = previewData[taxNumber] || taxNumber;
                const actualInvoiceDate = previewData[invoiceDate] || invoiceDate;
                const actualInvoiceTotal = previewData[invoiceTotal] || invoiceTotal;
                const actualTaxAmount = previewData[taxAmount] || taxAmount;
                
                console.log('ZATCA QR Data:', {
                  sellerName: actualSellerName,
                  taxNumber: actualTaxNumber,
                  invoiceDate: actualInvoiceDate,
                  invoiceTotal: actualInvoiceTotal,
                  taxAmount: actualTaxAmount
                });
                
                // إنشاء Base64 TLV
                const base64TLV = createTLVBase64(
                  actualSellerName,
                  actualTaxNumber,
                  actualInvoiceDate,
                  actualInvoiceTotal,
                  actualTaxAmount
                );
                
                // استخدام طريقة مختلفة لإنشاء QR لتحسين التوافق مع الطباعة
                if (typeof QRCode !== 'undefined') {
                  // استخدام مكتبة QRCode الحديثة إذا كانت متاحة
                  element.innerHTML = '';
                  new QRCode(element, {
                    text: base64TLV,
                    width: 128,
                    height: 128,
                    colorDark: "#000000",
                    colorLight: "#ffffff",
                    correctLevel: QRCode.CorrectLevel.L
                  });
                } else {
                  // الطريقة البديلة باستخدام qrcode-generator
                  try {
                    const qr = qrcode(0, 'L');
                    qr.addData(base64TLV);
                    qr.make();
                    element.innerHTML = qr.createImgTag(4);
                  } catch (qrError) {
                    console.error('خطأ في إنشاء QR:', qrError);
                    element.innerHTML = '<div style="padding: 10px; border: 1px solid #ccc; text-align: center;">QR Code</div>';
                  }
                }
              } catch (error) {
                console.error('خطأ في معالجة QR:', error);
                element.innerHTML = '<div style="padding: 10px; border: 1px solid #ccc; text-align: center;">QR Code</div>';
              }
            });
            
            // إضافة مرحلة تحقق من جميع الصور
            setTimeout(function() {
              // التأكد من تحميل جميع الصور
              document.querySelectorAll('img').forEach(function(img) {
                // إضافة خصائص مهمة للطباعة
                img.style.display = 'block';
                img.style.visibility = 'visible';
                img.setAttribute('data-print-ready', 'true');
              });
              console.log('تم تجهيز جميع الصور للطباعة');
            }, 500);
          });

          // إضافة معالجة خاصة للصور
          document.addEventListener('DOMContentLoaded', function() {
            // تحسين توافق الطباعة مع الصور
            document.querySelectorAll('img').forEach(function(img) {
              img.setAttribute('crossorigin', 'anonymous');
              img.addEventListener('load', function() {
                this.setAttribute('data-loaded', 'true');
              });
              img.addEventListener('error', function() {
                console.warn('فشل تحميل الصورة:', this.src);
              });
            });

            // مساعدة المتصفح على معالجة صور base64 بشكل أفضل
            setTimeout(function() {
              const allImagesLoaded = document.querySelectorAll('img[data-loaded="true"]').length;
              console.log('تم تحميل ' + allImagesLoaded + ' صورة بنجاح');
            }, 1000);
          });
        </script>
      </body>
      </html>
    `;
    
    
    previewWindow.document.open();
    previewWindow.document.write(htmlContent);
    previewWindow.document.close();
  };

  // إضافة عنصر نصي جديد
  const handleAddTextElement = () => {
    if (!template) return;
    
    const newElement: ITemplateElement = {
      id: `text_${Date.now()}`,
      elementType: 'text' as ElementType,
      content: 'نص جديد',
      style: {
        fontSize: 14,
        fontFamily: 'Cairo',
        fontWeight: 'normal',
        textAlign: 'right' as TextAlign,
        color: '#000000',
        backgroundColor: 'transparent',
        width: 150,
        height: 30,
        borderStyle: 'none',
        borderWidth: 0,
        borderColor: '#000000',
        padding: 5
      },
      position: {
        x: 100,
        y: 100,
        zIndex: 1
      }
    };
    
    const updatedTemplate = {
      ...template,
      elements: [...(template.elements || []), newElement]
    };
    
    setTemplate(updatedTemplate);
    setIsDirty(true);
  };

  // إضافة عنصر صورة جديد
  const handleAddImageElement = () => {
    if (!template) return;
    
    const newElement: ITemplateElement = {
      id: `image_${Date.now()}`,
      elementType: 'image' as ElementType,
      content: '', // سيتم تحديده عند تحميل الصورة
      style: {
        fontSize: 14,
        fontFamily: 'Cairo',
        fontWeight: 'normal',
        textAlign: 'center' as TextAlign,
        color: '#000000',
        backgroundColor: 'transparent',
        width: 150,
        height: 100,
        borderStyle: 'solid',
        borderWidth: 1,
        borderColor: '#dddddd',
        padding: 0
      },
      position: {
        x: 100,
        y: 100,
        zIndex: 1
      }
    };
    
    const updatedTemplate = {
      ...template,
      elements: [...(template.elements || []), newElement]
    };
    
    // إضافة عنصر وهمي للصورة في قائمة الحقول المختارة
    const imageField: IAvailableFieldOption = {
      field: `image_${newElement.id}`,
      label: 'صورة',
      description: 'عنصر صورة في التصميم',
      group: 'عناصر التصميم'
    };
    setSelectedFields(prev => [...prev, imageField]);
    
    // حفظ الحالة الحالية في مكدس التراجع
    addToUndoStack(template);
    
    setTemplate(updatedTemplate);
    setSelectedElementId(newElement.id);
    setIsDirty(true);
  };

  // دالة لتكوين TLV (Tag-Length-Value) لبيانات الفاتورة الإلكترونية وفقًا لمتطلبات هيئة الزكاة والضريبة والجمارك
  const createTLVBase64 = (
    sellerName: string,
    taxNumber: string,
    invoiceDate: string,
    invoiceTotal: string,
    taxAmount: string
  ): string => {
    // وظيفة مساعدة لتكوين TLV
    const toTLV = (tag: number, value: string): Uint8Array => {
      const tagByte = new Uint8Array([tag]);
      const valueBytes = new TextEncoder().encode(value);
      const lengthByte = new Uint8Array([valueBytes.length]);
      
      // دمج المصفوفات معًا
      const result = new Uint8Array(tagByte.length + lengthByte.length + valueBytes.length);
      result.set(tagByte, 0);
      result.set(lengthByte, tagByte.length);
      result.set(valueBytes, tagByte.length + lengthByte.length);
      
      return result;
    };

    // تنسيق التاريخ للتوافق مع صيغة ISO 8601 المطلوبة
    let formattedDate = invoiceDate;
    try {
      // إذا كان التاريخ بصيغة نصية غير ISO، نحاول تحويله
      if (!invoiceDate.includes('T') && !invoiceDate.includes('Z') && !invoiceDate.includes('+')) {
        const date = new Date(invoiceDate);
        // تنسيق التاريخ بصيغة ISO 8601 مع إضافة منطقة التوقيت المحلي
        formattedDate = date.toISOString().replace('Z', '+03:00');
      }
    } catch (e) {
      console.warn('خطأ في تنسيق التاريخ:', e);
    }

    // تنسيق المبالغ المالية (إزالة التنسيق والتأكد من وجود منزلتين عشريتين)
    const formatAmount = (amount: string): string => {
      // إزالة أي رموز عملة أو فواصل آلاف
      const numericValue = amount.replace(/[^0-9.-]/g, '');
      // التأكد من وجود منزلتين عشريتين
      return parseFloat(numericValue).toFixed(2);
    };

    // إنشاء TLV لكل حقل وفقًا لمتطلبات هيئة الزكاة والضريبة والجمارك (ZATCA)
    const tlv1 = toTLV(1, sellerName);
    const tlv2 = toTLV(2, taxNumber);
    const tlv3 = toTLV(3, formattedDate);
    const tlv4 = toTLV(4, formatAmount(invoiceTotal));
    const tlv5 = toTLV(5, formatAmount(taxAmount));
    
    // دمج جميع TLVs معًا
    const combinedLength = tlv1.length + tlv2.length + tlv3.length + tlv4.length + tlv5.length;
    const tlvBuffer = new Uint8Array(combinedLength);
    let offset = 0;
    
    tlvBuffer.set(tlv1, offset);
    offset += tlv1.length;
    tlvBuffer.set(tlv2, offset);
    offset += tlv2.length;
    tlvBuffer.set(tlv3, offset);
    offset += tlv3.length;
    tlvBuffer.set(tlv4, offset);
    offset += tlv4.length;
    tlvBuffer.set(tlv5, offset);
    
    // تحويل TLV إلى Base64
    return btoa(String.fromCharCode.apply(null, Array.from(tlvBuffer)));
  };

  // إضافة عنصر باركود QR للفاتورة الإلكترونية ZATCA
  const handleAddZATCAQRElement = () => {
    if (!template) return;
    
    const newElement: ITemplateElement = {
      id: `zatca_qr_${Date.now()}`,
      elementType: 'barcode' as ElementType,
      sourceField: 'misc.qrCode', // استخدام الحقل المعرف في availableFields
      content: 'zatca_qr', // ستتم معالجة هذه القيمة عند الطباعة
      style: {
        fontSize: 14,
        fontFamily: 'Cairo',
        fontWeight: 'normal',
        textAlign: 'center' as TextAlign,
        color: '#000000',
        backgroundColor: 'transparent',
        width: 150,
        height: 150,
        borderStyle: 'none',
        borderWidth: 0,
        borderColor: '#000000',
        padding: 5
      },
      position: {
        x: 100,
        y: 150,
        zIndex: 1
      },
      barcodeConfig: {
        barcodeType: 'qrcode',
        showText: false,
        isZATCAQR: true, // علامة خاصة لتمييز QR الفاتورة الإلكترونية
        zatcaFields: {
          sellerName: '{{branchInfo.companyName}}',
          vatNumber: '{{branchInfo.taxNumber}}',
          invoiceDate: '{{invoice.date}}',
          invoiceTotal: '{{totals.total}}',
          vatAmount: '{{totals.tax}}'
        }
      }
    };
    
    const updatedTemplate = {
      ...template,
      elements: [...(template.elements || []), newElement]
    };
    
    // إضافة حقل QR الفاتورة الإلكترونية إلى قائمة الحقول المختارة
    const qrField = availableFields.find(f => f.field === 'misc.qrCode');
    if (qrField) {
      setSelectedFields(prev => [...prev, qrField]);
    }
    
    // حفظ الحالة الحالية في مكدس التراجع
    addToUndoStack(template);
    
    setTemplate(updatedTemplate);
    setSelectedElementId(newElement.id);
    setIsDirty(true);
  };

  // إضافة عنصر باركود عادي
  const handleAddBarcodeElement = () => {
    if (!template) return;
    
    const newElement: ITemplateElement = {
      id: `barcode_${Date.now()}`,
      elementType: 'barcode' as ElementType,
      content: '12345678', // قيمة افتراضية
      style: {
        fontSize: 14,
        fontFamily: 'Cairo',
        fontWeight: 'normal',
        textAlign: 'center' as TextAlign,
        color: '#000000',
        backgroundColor: 'transparent',
        width: 150,
        height: 80,
        borderStyle: 'none',
        borderWidth: 0,
        borderColor: '#000000',
        padding: 5
      },
      position: {
        x: 100,
        y: 150,
        zIndex: 1
      },
      barcodeConfig: {
        barcodeType: 'qrcode',
        showText: true
      }
    };
    
    const updatedTemplate = {
      ...template,
      elements: [...(template.elements || []), newElement]
    };
    
    // إضافة عنصر وهمي للباركود في قائمة الحقول المختارة
    const barcodeField: IAvailableFieldOption = {
      field: `barcode_${newElement.id}`,
      label: 'باركود',
      description: 'عنصر باركود في التصميم',
      group: 'عناصر التصميم'
    };
    setSelectedFields(prev => [...prev, barcodeField]);
    
    // حفظ الحالة الحالية في مكدس التراجع
    addToUndoStack(template);
    
    setTemplate(updatedTemplate);
    setSelectedElementId(newElement.id);
    setIsDirty(true);
  };

  // إضافة عنصر جدول جديد
  const handleAddTableElement = () => {
    if (!template) return;
    const newElement: ITemplateElement = {
      id: `table_${Date.now()}`,
      elementType: 'table' as ElementType,
      sourceField: 'items',  // هذا الحقل ضروري لتعبئة بيانات الجدول من عناصر الفاتورة
      style: {
        fontSize: 12,
        fontFamily: 'Cairo',
        fontWeight: 'normal',
        textAlign: 'right' as TextAlign,
        color: '#000000',
        backgroundColor: 'transparent',
        width: 400,
        height: 200,
        borderStyle: 'solid',
        borderWidth: 1,
        borderColor: '#dddddd',
        padding: 0
      },
      position: {
        x: 50,
        y: 200,
        zIndex: 1
      },
      tableConfig: {
        showHeaders: true,
        columns: [
          { field: 'product', label: 'المنتج', width: 180, visible: true },
          { field: 'productId', label: 'معرف المنتج', width: 80, visible: false },
          { field: 'barcode', label: 'رمز المنتج/الباركود', width: 100, visible: true },
          { field: 'productName', label: 'اسم المنتج', width: 150, visible: false },
          { field: 'attributes', label: 'متغير المنتج', width: 150, visible: false },
          { field: 'quantity', label: 'الكمية', width: 80, visible: true },
          { field: 'unitName', label: 'الوحدة', width: 80, visible: true },
          { field: 'price', label: 'السعر', width: 80, visible: true },
          { field: 'priceWithTax', label: 'السعر شامل الضريبة', width: 80, visible: true },
          { field: 'subtotalBeforeTax', label: 'قبل الضريبة', width: 80, visible: true },
          { field: 'taxPercentage', label: 'الضريبة %', width: 80, visible: true },
          { field: 'taxAmount', label: 'قيمة الضريبة', width: 80, visible: true },
          { field: 'discount', label: 'الخصم', width: 80, visible: true },
          { field: 'tax', label: 'الضريبة', width: 80, visible: true },
          { field: 'total', label: 'الإجمالي', width: 90, visible: true }
        ],
        populateFromInvoice: true,
        compactView: false,
        fontSize: 9,
        headerFontSize: 10, // مقاس خط رأس الجدول
        styles: {
          headerBackground: '#f2f2f2',
          headerTextColor: '#333333',
          rowBackground: '#ffffff',
          rowTextColor: '#333333',
          alternateRowBackground: '#f9f9f9'
        }
      }
    };
    
    const updatedTemplate = {
      ...template,
      elements: [...(template.elements || []), newElement]
    };
    
    // إضافة عنصر وهمي للجدول في قائمة الحقول المختارة
    const tableField: IAvailableFieldOption = {
      field: `table_${newElement.id}`,
      label: 'جدول المنتجات',
      description: 'جدول يحتوي على تفاصيل المنتجات',
      group: 'عناصر التصميم'
    };
    setSelectedFields(prev => [...prev, tableField]);
    
    // حفظ الحالة الحالية في مكدس التراجع
    addToUndoStack(template);
    
    setTemplate(updatedTemplate);
    setSelectedElementId(newElement.id);
    setIsDirty(true);
  };

  // إضافة عنصر شكل جديد
  const handleAddShapeElement = () => {
    if (!template) return;
    
    const newElement: ITemplateElement = {
      id: `shape_${Date.now()}`,
      elementType: 'shape' as ElementType,
      content: 'rectangle', // rectangle, circle, line
      style: {
        fontSize: 14,
        fontFamily: 'Cairo',
        fontWeight: 'normal',
        textAlign: 'center' as TextAlign,
        color: '#000000',
        backgroundColor: '#f5f5f5',
        width: 100,
        height: 50,
        borderStyle: 'solid',
        borderWidth: 1,
        borderColor: '#000000',
        padding: 0
      },
      position: {
        x: 150,
        y: 250,
        zIndex: 1
      }
    };
    
    const updatedTemplate = {
      ...template,
      elements: [...(template.elements || []), newElement]
    };
    
    // إضافة عنصر وهمي للشكل في قائمة الحقول المختارة
    const shapeField: IAvailableFieldOption = {
      field: `shape_${newElement.id}`,
      label: `شكل (${newElement.content})`,
      description: 'عنصر شكل في التصميم',
      group: 'عناصر التصميم'
    };
    setSelectedFields(prev => [...prev, shapeField]);
    
    // حفظ الحالة الحالية في مكدس التراجع
    addToUndoStack(template);
    
    setTemplate(updatedTemplate);
    setSelectedElementId(newElement.id);
    setIsDirty(true);
  };

  // إضافة عنصر حقل جديد
  const handleAddFieldElement = () => {
    if (!template) return;
    
    // اختيار أول حقل متاح كقيمة افتراضية
    const defaultField = availableFields.length > 0 ? availableFields[0].field : 'customer.name';
    const defaultLabel = availableFields.length > 0 ? availableFields[0].label : 'اسم العميل';
    const defaultFieldOption = availableFields.find(f => f.field === defaultField);
    
    const newElement: ITemplateElement = {
      id: `field_${Date.now()}`,
      elementType: 'field' as ElementType,
      sourceField: defaultField,
      content: `[${defaultLabel}]`, // إضافة المحتوى لتوضيح اسم الحقل
      showFieldName: false, // بشكل افتراضي لا يتم عرض اسم الحقل
      style: {
        fontSize: 14,
        fontFamily: 'Cairo',
        fontWeight: 'normal',
        textAlign: 'right' as TextAlign,
        color: '#000000',
        backgroundColor: 'transparent',
        width: 150,
        height: 30,
        borderStyle: 'none',
        borderWidth: 0,
        borderColor: '#000000',
        padding: 5
      },
      position: {
        x: 100,
        y: 300,
        zIndex: 1
      }
    };
    
    const updatedTemplate = {
      ...template,
      elements: [...(template.elements || []), newElement]
    };
    
    // إضافة الحقل الافتراضي إلى قائمة الحقول المختارة
    if (defaultFieldOption) {
      setSelectedFields(prev => [...prev, defaultFieldOption]);
    }
    
    // حفظ الحالة الحالية في مكدس التراجع
    addToUndoStack(template);
    
    setTemplate(updatedTemplate);
    setSelectedElementId(newElement.id);
    setIsDirty(true);
  };

  // وظائف إدارة الحقول المختارة
  const handleAddSelectedField = (fieldName: string) => {
    const fieldOption = availableFields.find(f => f.field === fieldName);
    if (fieldOption) {
      // إزالة فحص التكرار للسماح بإضافة نفس الحقل عدة مرات
      setSelectedFields(prev => [...prev, fieldOption]);
      setIsDirty(true); // تعيين حالة التغيير لحفظ القائمة
    }
  };

  const handleRemoveSelectedField = (index: number) => {
    setSelectedFields(prev => prev.filter((_, i) => i !== index));
  };

  // إزالة حقل من قائمة الحقول المختارة بالاسم
  const handleRemoveSelectedFieldByName = (fieldName: string) => {
    setSelectedFields(prev => {
      const updatedFields = prev.filter(field => field.field !== fieldName);
      console.log('تمت إزالة الحقل من القائمة المختارة:', fieldName);
      return updatedFields;
    });
  };

  const handleClearSelectedFields = () => {
    setSelectedFields([]);
  };


  const handleAddFieldElementFromSelected = (field: IAvailableFieldOption) => {
    if (!template) return;
    
    const newElement: ITemplateElement = {
      id: `field_${Date.now()}`,
      elementType: 'field' as ElementType,
      sourceField: field.field,
      content: `[${field.label}]`,
      showFieldName: false,
      style: {
        fontSize: 14,
        fontFamily: 'Cairo',
        fontWeight: 'normal',
        textAlign: 'right' as TextAlign,
        color: '#000000',
        backgroundColor: 'transparent',
        width: 150,
        height: 30,
        borderStyle: 'none',
        borderWidth: 0,
        borderColor: '#000000',
        padding: 5
      },
      position: {
        x: 100,
        y: 100 + (template.elements.length * 40), // تباعد تلقائي للعناصر الجديدة
        zIndex: 1
      }
    };
    
    const updatedTemplate = {
      ...template,
      elements: [...(template.elements || []), newElement]
    };
    
    // حفظ الحالة الحالية في مكدس التراجع
    addToUndoStack(template);
    
    setTemplate(updatedTemplate);
    setSelectedElementId(newElement.id);
    setIsDirty(true);
  };

  // دالة للبحث عن حقل موجود في التصميم وتحديده
  const handleSelectExistingField = (field: IAvailableFieldOption) => {
    if (!template) return;
    
    // البحث عن جميع العناصر التي تحتوي على نفس الحقل
    const existingElements = template.elements.filter(element =>
      (element.elementType === 'field' || element.elementType === 'barcode') &&
      element.sourceField === field.field
    );
    
    if (existingElements.length > 0) {
      // إذا كان هناك عنصر واحد فقط، حدده مباشرة
      if (existingElements.length === 1) {
        const existingElement = existingElements[0];
        setSelectedElementId(existingElement.id);
        
        // التمرير إلى العنصر في منطقة التصميم
        const elementDiv = document.getElementById(`element-${existingElement.id}`);
        if (elementDiv) {
          elementDiv.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center'
          });
          
          // إضافة تأثير بصري مؤقت للإشارة إلى العنصر
          elementDiv.style.animation = 'pulse 1s ease-in-out 2';
          setTimeout(() => {
            elementDiv.style.animation = '';
          }, 2000);
        }
        
        console.log(`تم العثور على الحقل ${field.field} وتحديده`);
        return true;
      } else {
        // إذا كان هناك عدة عناصر، حدد الأول وأظهر تأثير بصري لجميعها
        setSelectedElementId(existingElements[0].id);
        
        existingElements.forEach((element, index) => {
          const elementDiv = document.getElementById(`element-${element.id}`);
          if (elementDiv) {
            // إضافة تأثير بصري مؤقت لجميع العناصر المطابقة
            setTimeout(() => {
              elementDiv.style.animation = 'pulse 1s ease-in-out 2';
              setTimeout(() => {
                elementDiv.style.animation = '';
              }, 2000);
            }, index * 500); // تأخير متدرج للعناصر المتعددة
          }
        });
        
        // التمرير إلى أول عنصر
        const firstElementDiv = document.getElementById(`element-${existingElements[0].id}`);
        if (firstElementDiv) {
          firstElementDiv.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center'
          });
        }
        
        console.log(`تم العثور على ${existingElements.length} عناصر للحقل ${field.field}`);
        return true;
      }
    }
    
    return false;
  };

  
  // دالة للعثور على الحقل الموجود وتحديده فقط (بدون إضافة حقل جديد)
  const handleSelectedFieldClick = (field: IAvailableFieldOption) => {
    if (!template) return;
    
    // جميع الحقول الآن تستخدم معرف العنصر، لذلك ابحث بالمعرف المباشر
    const foundElement = template.elements.find(element => element.id === field.field);
    
    if (foundElement) {
      setSelectedElementId(foundElement.id);
      
      // التمرير إلى العنصر في منطقة التصميم
      const elementDiv = document.getElementById(`element-${foundElement.id}`);
      if (elementDiv) {
        elementDiv.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center'
        });
        
        // إضافة تأثير بصري مؤقت للإشارة إلى العنصر
        elementDiv.style.animation = 'pulse 1s ease-in-out 2';
        setTimeout(() => {
          elementDiv.style.animation = '';
        }, 2000);
      }
      
      console.log(`تم العثور على العنصر ${foundElement.id} وتحديده`);
    }
  };
  
  // These functions already defined above

  // التراجع عن آخر تغيير
  const handleUndo = () => {
    if (undoStack.length === 0 || !template) return;
    
    // حفظ الحالة الحالية في مكدس الإعادة
    setRedoStack(prev => [...prev, JSON.parse(JSON.stringify(template))]);
    
    // استرجاع آخر حالة من مكدس التراجع
    const lastState = undoStack[undoStack.length - 1];
    setTemplate(JSON.parse(JSON.stringify(lastState)));
    
    // إزالة آخر حالة من مكدس التراجع
    setUndoStack(prev => prev.slice(0, -1));
    
    setIsDirty(true);
  };

  // إعادة آخر تغيير تم التراجع عنه
  const handleRedo = () => {
    if (redoStack.length === 0) return;
    
    // حفظ الحالة الحالية في مكدس التراجع
    if (template) {
      setUndoStack(prev => [...prev, JSON.parse(JSON.stringify(template))]);
    }
    
    // استرجاع آخر حالة من مكدس الإعادة
    const nextState = redoStack[redoStack.length - 1];
    setTemplate(JSON.parse(JSON.stringify(nextState)));
    
    // إزالة آخر حالة من مكدس الإعادة
    setRedoStack(prev => prev.slice(0, -1));
    
    setIsDirty(true);
  };

  // تقريب القيمة إلى الشبكة عند التفعيل
  const snapToGridValue = (value: number): number => {
    if (!snapToGrid) return value;
    return Math.round(value / gridSize) * gridSize;
  };
  
  // معالجة النقر على عنصر
  const handleElementClick = (elementId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedElementId(elementId);
    
    // إزالة التحديد عند النقر على منطقة التصميم خارج العناصر
    if (e.currentTarget === designAreaRef.current) {
      setSelectedElementId(null);
    }
  };
  // وظيفة محسنة لبدء سحب العنصر
  const handleDragStart = (elementId: string, e: React.MouseEvent<HTMLDivElement>) => {
    if (!template) return;
    
    console.log(`بدء سحب العنصر: ${elementId}`);
    
    // منع السلوك الافتراضي للمتصفح بشكل كامل
    e.stopPropagation();
    e.preventDefault();
    
    // البحث عن العنصر في القالب
    const element = template.elements.find(el => el.id === elementId);
    if (!element) return;
    
    // حساب نقطة الضغط داخل العنصر
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;
    
    console.log(`نقطة الضغط: (${offsetX}, ${offsetY}), العنصر عند: (${element.position.x}, ${element.position.y})`);
    
    // تحديث حالة السحب
    setDragOffset({ x: offsetX, y: offsetY });
    setDraggedElementId(elementId);
    setSelectedElementId(elementId);
    setResizing(false);
    setResizeDirection(null);
    
    // تطبيق CSS classes للتحكم في السحب بدلاً من inline styles
    document.body.classList.add('dragging-active');
    
    // إضافة class للعنصر المسحوب
    const dragEl = document.getElementById(`element-${elementId}`);
    if (dragEl) {
      dragEl.classList.add('dragging-element');
    }
    
    // إضافة مستمعي أحداث على مستوى النافذة لضمان استمرار تلقي الأحداث
    document.addEventListener('mousemove', handleMouseMove, { capture: true });
    document.addEventListener('mouseup', handleMouseUp, { capture: true });
    
    // تعيين أنماط على مستوى الصفحة لتحسين السحب
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'grabbing';
  };
  
  // تحسين معالج تغيير الحجم
  const handleResizeStart = (elementId: string, direction: string, e: React.MouseEvent) => {
    if (!template) return;
    
    // إيقاف السلوك الافتراضي للمتصفح
    e.stopPropagation();
    e.preventDefault();
    
    // تعيين حالة تغيير الحجم
    setDraggedElementId(elementId);
    setResizing(true);
    setResizeDirection(direction);
    setSelectedElementId(elementId);
    
    // تعطيل تحديد النص
    document.body.style.userSelect = 'none';
    document.body.style.cursor = direction.includes('e') || direction.includes('w') ? 'ew-resize' : 'ns-resize';
    
    // إضافة مستمعي الأحداث لضمان استمرار تلقي الأحداث
    window.addEventListener('mousemove', handleMouseMove, { capture: true });
    window.addEventListener('mouseup', handleMouseUp, { capture: true });
  };
  
  // وظيفة محسنة لإنهاء السحب
  const handleMouseUp = (e: MouseEvent) => {
    // منع السلوك الافتراضي للمتصفح
    e.preventDefault();
    e.stopPropagation();
    
    // إزالة الفئة من الجسم بعد انتهاء السحب
    document.body.classList.remove('dragging-active');
    
    // إعادة تعيين أنماط العنصر المسحوب
    if (draggedElementId) {
      const elem = document.getElementById(`element-${draggedElementId}`);
      if (elem) {
        elem.style.zIndex = '';
      }
    }
    
    // إزالة class من العنصر المسحوب
    if (draggedElementId) {
      const dragElement = document.getElementById(`element-${draggedElementId}`);
      if (dragElement) {
        dragElement.classList.remove('dragging-element');
      }
    }
    
    // إزالة مستمعي الأحداث
    window.removeEventListener('mousemove', handleMouseMove, { capture: true });
    window.removeEventListener('mouseup', handleMouseUp, { capture: true });
    
    // إعادة تعيين حالات السحب
    setDraggedElementId(null);
    setResizing(false);
    setResizeDirection(null);
    
    // إزالة خطوط الإرشاد
    setActiveGuides([]);
    
    // تحديث مكدس التراجع
    // إعادة أنماط الصفحة إلى وضعها الطبيعي
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
    
    // تعيين حالة التغيير والتحديث
    setIsDirty(true);
    
    if (template) {
      addToUndoStack(template);
    }
    
    console.log(`انتهاء السحب، حالة التغيير: ${true}`);
  };
  
  // تحسين معالج حركة الماوس
  const handleMouseMove = (e: MouseEvent) => {
    // إيقاف السلوك الافتراضي للمتصفح
    e.preventDefault();
    e.stopPropagation();
    
    // التحقق من وجود عنصر للسحب والقالب
    if (!draggedElementId || !template) return;
    
    // التأكد من منطقة التصميم
    const designArea = designAreaRef.current;
    if (!designArea) return;
    
    // الحصول على معلومات العنصر
    const designRect = designArea.getBoundingClientRect();
    const element = template.elements.find(el => el.id === draggedElementId);
    if (!element) return;
    
    // تسجيل معلومات عن حركة الماوس
    console.log(`حركة الماوس: (${e.clientX}, ${e.clientY}), العنصر: ${draggedElementId}`);
    
    // حالة تغيير الحجم
    if (resizing && resizeDirection) {
      let newWidth = element.style.width;
      let newHeight = element.style.height;
      let newX = element.position.x;
      let newY = element.position.y;
      
      // حساب الموقع النسبي داخل منطقة التصميم
      const relativeX = e.clientX - designRect.left;
      const relativeY = e.clientY - designRect.top;
      
      // الحد الأدنى للأبعاد
      const minSize = 20;
      
      // حساب الأبعاد الجديدة حسب اتجاه التغيير
      switch (resizeDirection) {
        case 'e':  // يمين
          newWidth = Math.max(minSize, snapToGridValue(relativeX - element.position.x));
          break;
          
        case 'w':  // يسار
          const widthChangeW = element.position.x - relativeX;
          if (element.style.width + widthChangeW >= minSize) {
            newWidth = snapToGridValue(element.style.width + widthChangeW);
            newX = snapToGridValue(relativeX);
          }
          break;
          
        case 's':  // أسفل
          newHeight = Math.max(minSize, snapToGridValue(relativeY - element.position.y));
          break;
          
        case 'n':  // أعلى
          const heightChangeN = element.position.y - relativeY;
          if (element.style.height + heightChangeN >= minSize) {
            newHeight = snapToGridValue(element.style.height + heightChangeN);
            newY = snapToGridValue(relativeY);
          }
          break;
          
        case 'se':  // الزاوية السفلية اليمنى
          newWidth = Math.max(minSize, snapToGridValue(relativeX - element.position.x));
          newHeight = Math.max(minSize, snapToGridValue(relativeY - element.position.y));
          break;
          
        case 'sw':  // الزاوية السفلية اليسرى
          const widthChangeSW = element.position.x - relativeX;
          if (element.style.width + widthChangeSW >= minSize) {
            newWidth = snapToGridValue(element.style.width + widthChangeSW);
            newX = snapToGridValue(relativeX);
          }
          newHeight = Math.max(minSize, snapToGridValue(relativeY - element.position.y));
          break;
          
        case 'ne':  // الزاوية العلوية اليمنى
          newWidth = Math.max(minSize, snapToGridValue(relativeX - element.position.x));
          const heightChangeNE = element.position.y - relativeY;
          if (element.style.height + heightChangeNE >= minSize) {
            newHeight = snapToGridValue(element.style.height + heightChangeNE);
            newY = snapToGridValue(relativeY);
          }
          break;
          
        case 'nw':  // الزاوية العلوية اليسرى
          const widthChangeNW = element.position.x - relativeX;
          const heightChangeNW = element.position.y - relativeY;
          
          if (element.style.width + widthChangeNW >= minSize) {
            newWidth = snapToGridValue(element.style.width + widthChangeNW);
            newX = snapToGridValue(relativeX);
          }
          
          if (element.style.height + heightChangeNW >= minSize) {
            newHeight = snapToGridValue(element.style.height + heightChangeNW);
            newY = snapToGridValue(relativeY);
          }
          break;
      }

      // تطبيق خطوط المحاذاة إذا كانت مفعلة
      let alignGuides: { type: string; position: number }[] = [];
      
      if (showGuides) {
        // اجمع العناصر الأخرى لمقارنة المحاذاة
        const otherElements = template.elements.filter(el => el.id !== draggedElementId);
        
        otherElements.forEach(otherEl => {
          // محاذاة أفقية
          if (Math.abs(newX - otherEl.position.x) < 8) {
            newX = otherEl.position.x;
            alignGuides.push({ type: 'vertical', position: newX });
          }
          
          const thisRight = newX + newWidth;
          const otherRight = otherEl.position.x + otherEl.style.width;
          if (Math.abs(thisRight - otherRight) < 8) {
            newX = otherRight - newWidth;
            alignGuides.push({ type: 'vertical', position: otherRight });
          }
          
          const thisCenter = newX + newWidth / 2;
          const otherCenter = otherEl.position.x + otherEl.style.width / 2;
          if (Math.abs(thisCenter - otherCenter) < 8) {
            newX = otherCenter - newWidth / 2;
            alignGuides.push({ type: 'vertical', position: otherCenter });
          }
          
          // محاذاة رأسية
          if (Math.abs(newY - otherEl.position.y) < 8) {
            newY = otherEl.position.y;
            alignGuides.push({ type: 'horizontal', position: newY });
          }
          
          const thisBottom = newY + newHeight;
          const otherBottom = otherEl.position.y + otherEl.style.height;
          if (Math.abs(thisBottom - otherBottom) < 8) {
            newY = otherBottom - newHeight;
            alignGuides.push({ type: 'horizontal', position: otherBottom });
          }
          
          const thisMiddle = newY + newHeight / 2;
          const otherMiddle = otherEl.position.y + otherEl.style.height / 2;
          if (Math.abs(thisMiddle - otherMiddle) < 8) {
            newY = otherMiddle - newHeight / 2;
            alignGuides.push({ type: 'horizontal', position: otherMiddle });
          }
        });
        
        // عرض خطوط الإرشاد
        setActiveGuides(alignGuides);
      }
       
      // تحديث أبعاد وموقع العنصر
      const updatedElements = template.elements.map(el => {
        if (el.id === draggedElementId) {
          return {
            ...el,
            style: {
              ...el.style,
              width: newWidth,
              height: newHeight
            },
            position: {
              ...el.position,
              x: newX,
              y: newY
            }
          };
        }
        return el;
      });
      
      setTemplate({
        ...template,
        elements: updatedElements
      });
    } else {
      // حالة التحريك
      // حساب الموقع الجديد نسبة إلى منطقة التصميم
      let newX = e.clientX - designRect.left - dragOffset.x;
      let newY = e.clientY - designRect.top - dragOffset.y;
      
      console.log(`حساب الموقع الجديد: (${newX}, ${newY}), إزاحة: (${dragOffset.x}, ${dragOffset.y})`);
      
      // تقريب القيم إلى الشبكة إذا كان التقريب مفعل
      newX = snapToGridValue(newX);
      newY = snapToGridValue(newY);
      
      // التأكد من أن العنصر لا يخرج من حدود منطقة التصميم
      newX = Math.max(0, newX);
      newY = Math.max(0, newY);
      
      // عرض خطوط الإرشاد عند التحريك
      if (showGuides) {
        // الحصول على العنصر الذي يتم سحبه
        const draggedElement = template.elements.find(el => el.id === draggedElementId);
        if (!draggedElement) return;
        
        // تجميع مواقع كل العناصر الأخرى للمقارنة
        const otherElements = template.elements.filter(el => el.id !== draggedElementId);
        let alignGuides: { type: string; position: number }[] = [];
        
        otherElements.forEach(otherEl => {
          // محاذاة أفقية
          if (Math.abs(newX - otherEl.position.x) < 8) {
            newX = otherEl.position.x;
            alignGuides.push({ type: 'vertical', position: newX });
          }
          
          const thisRight = newX + draggedElement.style.width;
          const otherRight = otherEl.position.x + otherEl.style.width;
          if (Math.abs(thisRight - otherRight) < 8) {
            newX = otherRight - draggedElement.style.width;
            alignGuides.push({ type: 'vertical', position: otherRight });
          }
          
          const thisCenter = newX + draggedElement.style.width / 2;
          const otherCenter = otherEl.position.x + otherEl.style.width / 2;
          if (Math.abs(thisCenter - otherCenter) < 8) {
            newX = otherCenter - draggedElement.style.width / 2;
            alignGuides.push({ type: 'vertical', position: otherCenter });
          }
          
          // محاذاة رأسية
          if (Math.abs(newY - otherEl.position.y) < 8) {
            newY = otherEl.position.y;
            alignGuides.push({ type: 'horizontal', position: newY });
          }
          
          const thisBottom = newY + draggedElement.style.height;
          const otherBottom = otherEl.position.y + otherEl.style.height;
          if (Math.abs(thisBottom - otherBottom) < 8) {
            newY = otherBottom - draggedElement.style.height;
            alignGuides.push({ type: 'horizontal', position: otherBottom });
          }
          
          const thisMiddle = newY + draggedElement.style.height / 2;
          const otherMiddle = otherEl.position.y + otherEl.style.height / 2;
          if (Math.abs(thisMiddle - otherMiddle) < 8) {
            newY = otherMiddle - draggedElement.style.height / 2;
            alignGuides.push({ type: 'horizontal', position: otherMiddle });
          }
        });
        
        // عرض خطوط الإرشاد
        setActiveGuides(alignGuides);
      }
      
      // تحديث موقع العنصر
      const updatedElements = template.elements.map(el => {
        if (el.id === draggedElementId) {
          return {
            ...el,
            position: {
              ...el.position,
              x: newX,
              y: newY
            }
          };
        }
        return el;
      });
  // This function also calls handleMouseUp, so we need to make sure it's defined first
      
      setTemplate({
        ...template,
        elements: updatedElements
      });
    }
    
    setIsDirty(true);
  };
  
  // This function has been removed as we're now using mouse events
  // The functionality has been merged into handleMouseUp
  
  // حذف العنصر المحدد
  const handleDeleteElement = () => {
    if (!selectedElementId || !template) return;
    
    // حفظ الحالة الحالية في مكدس التراجع
    addToUndoStack(template);
    
    const updatedElements = template.elements.filter(
      element => element.id !== selectedElementId
    );
    
    setTemplate({
      ...template,
      elements: updatedElements
    });
    
    setSelectedElementId(null);
    setIsDirty(true);
  };
  
  // تحديث خصائص العنصر المحدد
  const handleUpdateSelectedElement = (updates: Partial<ITemplateElement>) => {
    if (!selectedElementId || !template) return;
    
    const updatedElements = template.elements.map(element => {
      if (element.id === selectedElementId) {
        return {
          ...element,
          ...updates,
          style: {
            ...element.style,
            ...(updates.style || {})
          },
          position: {
            ...element.position,
            ...(updates.position || {})
          }
        };
      }
      return element;
    });
    
    const updatedTemplate = {
      ...template,
      elements: updatedElements
    };
    
    setTemplate(updatedTemplate);
    setIsDirty(true);
    
    // تنظيف فوري للحقول المختارة عند تحديث العنصر
    setTimeout(() => cleanupSelectedFields(updatedTemplate), 0);
  };
  
  // الحصول على العنصر المحدد
  const getSelectedElement = () => {
    if (!selectedElementId || !template) return null;
    return template.elements.find(element => element.id === selectedElementId) || null;
  };

  // إضافة مفاتيح اختصار لتحريك العناصر بدقة
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!selectedElementId || !template) return;
      
      // التأكد من أننا لسنا في وسط تحرير نص
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }
      
      const element = template.elements.find(el => el.id === selectedElementId);
      if (!element) return;
      
      // حجم الخطوة: 1 بيكسل عادي، 10 بيكسل مع Shift
      const step = e.shiftKey ? 10 : 1;
      let updatedElements = [...template.elements];
      let needsUpdate = false;
      
      updatedElements = updatedElements.map(el => {
        if (el.id === selectedElementId) {
          const newEl = { ...el };
          
          // تحريك العنصر باستخدام مفاتيح الأسهم
          if (e.key === 'ArrowRight') {
            newEl.position = { ...newEl.position, x: newEl.position.x + step };
            needsUpdate = true;
          } else if (e.key === 'ArrowLeft') {
            newEl.position = { ...newEl.position, x: Math.max(0, newEl.position.x - step) };
            needsUpdate = true;
          } else if (e.key === 'ArrowDown') {
            newEl.position = { ...newEl.position, y: newEl.position.y + step };
            needsUpdate = true;
          } else if (e.key === 'ArrowUp') {
            newEl.position = { ...newEl.position, y: Math.max(0, newEl.position.y - step) };
            needsUpdate = true;
          }
          
          // تغيير حجم العنصر مع Alt + الأسهم
          if (e.altKey) {
            if (e.key === 'ArrowRight') {
              newEl.style = { ...newEl.style, width: newEl.style.width + step };
              needsUpdate = true;
            } else if (e.key === 'ArrowLeft') {
              newEl.style = { ...newEl.style, width: Math.max(10, newEl.style.width - step) };
              needsUpdate = true;
            } else if (e.key === 'ArrowDown') {
              newEl.style = { ...newEl.style, height: newEl.style.height + step };
              needsUpdate = true;
            } else if (e.key === 'ArrowUp') {
              newEl.style = { ...newEl.style, height: Math.max(10, newEl.style.height - step) };
              needsUpdate = true;
            }
          }
          
          return newEl;
        }
        return el;
      });
      
      if (needsUpdate) {
        e.preventDefault();
        setTemplate({
          ...template,
          elements: updatedElements
        });
        setIsDirty(true);
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedElementId, template]);
  
  // إضافة مستمعات للأحداث المخصصة من مكون إصلاح السحب
  useEffect(() => {
    // معالجة حدث السحب المستمر
    const handleElementDragged = (e: any) => {
      if (!template) return;
      
      const { elementId, position } = e.detail;
      console.log(`تلقي حدث سحب للعنصر ${elementId} إلى الموقع (${position.x}, ${position.y})`);
      
      // تحديث حالة العنصر أثناء السحب (بدون تسجيل في التاريخ)
      const updatedElements = template.elements.map(el =>
        el.id === elementId
          ? { ...el, position: { ...el.position, x: position.x, y: position.y } }
          : el
      );
      
      // تحديث القالب بدون تعيين isDirty لأن هذا تحديث مؤقت أثناء السحب
      setTemplate({
        ...template,
        elements: updatedElements
      });
    };
    
    // معالجة حدث انتهاء السحب
    const handleElementMoved = (e: any) => {
      if (!template) return;
      
      const { elementId, position, isDirty = true } = e.detail;
      console.log(`تلقي حدث انتهاء السحب للعنصر ${elementId} إلى الموقع النهائي (${position.x}, ${position.y}). تعيين حالة التغيير: ${isDirty}`);
      
      // تحديث موضع العنصر بشكل نهائي عند انتهاء السحب
      if (template) {
        // تحديث موضع العنصر نهائياً وتعيين حالة التغيير
        const updatedElements = template.elements.map(el =>
          el.id === elementId
            ? { ...el, position: { ...el.position, x: position.x, y: position.y } }
            : el
        );
        
        setTemplate({
          ...template,
          elements: updatedElements
        });
        
        // تعيين حالة التغيير لإظهار زر الحفظ
        if (isDirty) {
          console.log('تعيين حالة التغيير إلى true لإظهار زر الحفظ');
          setIsDirty(true);
        }
      }
    };
    
    // إضافة مستمعي الأحداث
    document.addEventListener('elementDragged', handleElementDragged as EventListener);
    document.addEventListener('elementMoved', handleElementMoved as EventListener);
    
    return () => {
      // إزالة مستمعي الأحداث عند تفريغ المكون
      document.removeEventListener('elementDragged', handleElementDragged as EventListener);
      document.removeEventListener('elementMoved', handleElementMoved as EventListener);
    };
  }, [template]);

  // تنظيف قائمة الحقول المختارة عند تغيير العناصر
  useEffect(() => {
    if (!template || !template.elements) return;
    
    // إضافة تأخير قصير لتجنب الحالات الانتقالية أثناء تحديث العناصر
    const timeoutId = setTimeout(() => {
      // إنشاء قائمة الحقول المحددة بناءً على العناصر الموجودة في التصميم
      const cleanedFields: IAvailableFieldOption[] = [];
      
      template.elements.forEach(element => {
        let fieldOption: IAvailableFieldOption | undefined;
        
        if (element.elementType === 'field' && element.sourceField) {
          // للحقول العادية، استخدم معرف العنصر كمفتاح فريد
          fieldOption = availableFields.find(f => f.field === element.sourceField);
          if (fieldOption) {
            fieldOption = {
              ...fieldOption,
              field: element.id // استخدام معرف العنصر بدلاً من sourceField
            };
          }
        } else if (element.elementType === 'barcode' && element.sourceField) {
          // للباركود مع sourceField (مثل الفاتورة الإلكترونية)، استخدم معرف العنصر
          fieldOption = availableFields.find(f => f.field === element.sourceField);
          if (fieldOption) {
            fieldOption = {
              ...fieldOption,
              field: element.id // استخدام معرف العنصر بدلاً من sourceField
            };
          }
        } else if (element.elementType === 'text') {
          fieldOption = {
            field: element.id,
            label: 'نص',
            description: 'عنصر نص في التصميم',
            group: 'عناصر التصميم'
          };
        } else if (element.elementType === 'image') {
          fieldOption = {
            field: element.id,
            label: 'صورة',
            description: 'عنصر صورة في التصميم',
            group: 'عناصر التصميم'
          };
        } else if (element.elementType === 'shape') {
          fieldOption = {
            field: element.id,
            label: 'شكل',
            description: 'عنصر شكل في التصميم',
            group: 'عناصر التصميم'
          };
        } else if (element.elementType === 'barcode' && !element.sourceField) {
          fieldOption = {
            field: element.id,
            label: 'باركود',
            description: 'عنصر باركود في التصميم',
            group: 'عناصر التصميم'
          };
        } else if (element.elementType === 'table') {
          fieldOption = {
            field: element.id,
            label: 'جدول',
            description: 'جدول المنتجات في التصميم',
            group: 'عناصر التصميم'
          };
        }
        
        if (fieldOption) {
          cleanedFields.push(fieldOption);
        }
      });
      
      // تصفية قائمة الحقول المختارة بناءً على الاستخدام الفعلي
      setSelectedFields(prevFields => {
        // التحقق من وجود تغيير
        const hasChanged = cleanedFields.length !== prevFields.length ||
          !cleanedFields.every((field, index) => prevFields[index]?.field === field.field);
        
        if (hasChanged) {
          console.log('تنظيف قائمة الحقول المختارة:', {
            'القائمة السابقة': prevFields.map(f => f.field),
            'القائمة الجديدة': cleanedFields.map(f => f.field)
          });
          return cleanedFields;
        }
        
        return prevFields;
      });
    }, 100); // تأخير 100ms لتجنب الحالات الانتقالية
    
    // تنظيف timeout عند إلغاء المكون أو تغيير التبعيات
    return () => clearTimeout(timeoutId);
  }, [template?.elements]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="500px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button startIcon={<BackIcon />} onClick={handleGoBack}>
          العودة
        </Button>
      </Box>
    );
  }

  if (!template) {
    return (
      <Box p={3}>
        <Alert severity="error" sx={{ mb: 3 }}>
          لم يتم العثور على القالب
        </Alert>
        <Button startIcon={<BackIcon />} onClick={handleGoBack}>
          العودة
        </Button>
      </Box>
    );
  }
return (
  <Box p={2}>
    {/* تم إزالة مكون GlobalDragFix واستبداله بالحل البرمجي في dragFix.js */}
    
      
      {/* شريط الأدوات العلوي */}
      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Button startIcon={<BackIcon />} onClick={handleGoBack}>
              العودة
            </Button>
          </Grid>
          <Grid item xs>
            <Typography variant="h6" component="h1">
              {template.name} - محرر القالب
            </Typography>
          </Grid>
          <Grid item>
            <Button
              startIcon={<PreviewIcon />}
              color="info"
              variant="outlined"
              onClick={handlePreviewTemplate}
              sx={{ mr: 1 }}
            >
              معاينة
            </Button>
            <Button
              startIcon={<SaveIcon />}
              color="primary"
              variant="contained"
              onClick={handleSaveTemplate}
              disabled={!isDirty}
            >
              حفظ
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* منطقة تحرير القالب */}
      <Grid container spacing={2}>
        {/* شريط الأدوات الجانبي */}
        <Grid item xs={12} md={2}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              العناصر
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Tooltip title="نص" placement="right">
                <Button 
                  startIcon={<TextIcon />} 
                  variant="outlined" 
                  fullWidth
                  onClick={handleAddTextElement}
                >
                  نص
                </Button>
              </Tooltip>
              <Tooltip title="حقل" placement="right">
                <Button 
                  startIcon={<FieldIcon />} 
                  variant="outlined" 
                  fullWidth
                  onClick={handleAddFieldElement}
                >
                  حقل
                </Button>
              </Tooltip>
              <Tooltip title="صورة" placement="right">
                <Button 
                  startIcon={<ImageIcon />} 
                  variant="outlined" 
                  fullWidth
                  onClick={handleAddImageElement}
                >
                  صورة
                </Button>
              </Tooltip>
              <Tooltip title="باركود" placement="right">
                <Button
                  startIcon={<BarcodeIcon />}
                  variant="outlined"
                  fullWidth
                  onClick={handleAddBarcodeElement}
                >
                  باركود
                </Button>
              </Tooltip>
              <Tooltip title="QR للفاتورة الإلكترونية" placement="right">
                <Button
                  startIcon={<BarcodeIcon />}
                  color="secondary"
                  variant="outlined"
                  fullWidth
                  onClick={handleAddZATCAQRElement}
                >
                  QR الفاتورة الإلكترونية
                </Button>
              </Tooltip>
              <Tooltip title="جدول" placement="right">
                <Button 
                  startIcon={<TableIcon />} 
                  variant="outlined" 
                  fullWidth
                  onClick={handleAddTableElement}
                >
                  جدول
                </Button>
              </Tooltip>
              <Tooltip title="شكل" placement="right">
                <Button 
                  startIcon={<ShapeIcon />} 
                  variant="outlined" 
                  fullWidth
                  onClick={handleAddShapeElement}
                >
                  شكل
                </Button>
              </Tooltip>
              
              <Divider sx={{ my: 1 }} />
              
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                <Tooltip title="تراجع">
                  <IconButton onClick={handleUndo}>
                    <UndoIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="إعادة">
                  <IconButton onClick={handleRedo}>
                    <RedoIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Paper>
        </Grid>
        
        {/* قائمة الحقول المختارة */}
        <Grid item xs={12} md={1.5}>
          <Paper elevation={2} sx={{ p: 2, height: 'calc(100vh - 200px)', overflow: 'auto' }}>
            <Typography variant="subtitle1" gutterBottom>
              الحقول المختارة
            </Typography>
            <Divider sx={{ my: 1 }} />
            
            {selectedFields.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
                لم يتم اختيار أي حقول بعد
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {selectedFields.map((field, index) => (
                  <Paper
                    key={`${field.field}-${index}`}
                    elevation={1}
                    sx={{
                      p: 1,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      backgroundColor: '#f8f9fa',
                      position: 'relative',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: '#e3f2fd',
                        borderColor: '#1976d2'
                      }
                    }}
                    onClick={() => handleSelectedFieldClick(field)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Chip
                        label={index + 1}
                        size="small"
                        color="primary"
                        sx={{ minWidth: '24px', height: '20px', fontSize: '0.7rem' }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                        <Typography variant="caption" sx={{
                          fontWeight: 'bold',
                          mb: 0.5,
                          fontSize: '0.75rem',
                          lineHeight: 1.2,
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          hyphens: 'auto'
                        }}>
                          {field.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{
                          display: 'block',
                          mb: 0.5,
                          fontSize: '0.65rem',
                          lineHeight: 1.1,
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word'
                        }}>
                          {field.field}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{
                          fontSize: '0.6rem',
                          lineHeight: 1.1,
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word'
                        }}>
                          {field.group}
                        </Typography>
                        {field.description && (
                          <Typography variant="caption" color="text.secondary" sx={{
                            display: 'block',
                            mt: 0.5,
                            fontSize: '0.55rem',
                            lineHeight: 1.1,
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word'
                          }}>
                            {field.description}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 1, textAlign: 'center', fontSize: '0.65rem' }}>
                      انقر لتحديد الحقل في التصميم
                    </Typography>
                  </Paper>
                ))}
              </Box>
            )}
            
          </Paper>
        </Grid>
        
        {/* منطقة تصميم القالب */}
        <Grid item xs={12} md={8.5}>
          <Paper
            elevation={3}
            sx={{
              p: 2,
              height: 'calc(100vh - 200px)',
              position: 'relative',
              overflow: 'auto', // السماح بالتمرير في كلا الاتجاهين
              backgroundColor: '#f5f5f5',
              border: '1px solid #ddd',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            {/* خيارات العرض والتحرير */}
            <Box sx={{
              mb: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              {/* مؤشر أبعاد الورق */}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 0.5,
                backgroundColor: 'primary.main',
                color: 'white',
                borderRadius: 1,
                fontSize: '0.8rem'
              }}>
                <Typography variant="caption" sx={{ color: 'inherit' }}>
                  {template.paperSize === 'A4' ? 'A4' :
                   template.paperSize === 'A5' ? 'A5' :
                   template.paperSize === 'A6' ? 'A6' :
                   template.paperSize === 'thermal80mm' ? 'حراري 80مم' :
                   template.paperSize === 'thermal58mm' ? 'حراري 58مم' :
                   'مخصص'} - {template.orientation === 'portrait' ? 'عمودي' : 'أفقي'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'inherit', opacity: 0.8 }}>
                  ({template.orientation === 'landscape' ?
                    (template.paperSize === 'A4' ? '297×210' :
                     template.paperSize === 'A5' ? '210×148' :
                     template.paperSize === 'A6' ? '148×105' :
                     template.paperSize === 'thermal80mm' ? '80×297' :
                     template.paperSize === 'thermal58mm' ? '58×297' :
                     `${template.customHeight || 297}×${template.customWidth || 210}`) :
                    (template.paperSize === 'A4' ? '210×297' :
                     template.paperSize === 'A5' ? '148×210' :
                     template.paperSize === 'A6' ? '105×148' :
                     template.paperSize === 'thermal80mm' ? '80×297' :
                     template.paperSize === 'thermal58mm' ? '58×297' :
                     `${template.customWidth || 210}×${template.customHeight || 297}`)}مم)
                </Typography>
              </Box>
              
              {/* إضافة تلميح للتحكم باستخدام لوحة المفاتيح */}
              <Tooltip title="استخدم مفاتيح الأسهم لتحريك العناصر بدقة. اضغط Shift + الأسهم للتحريك بمقدار 10 بكسل، و Alt + الأسهم لتغيير الحجم">
                <Button
                  size="small"
                  variant="text"
                  startIcon={<InfoIcon />}
                >
                  تلميحات التحكم
                </Button>
              </Tooltip>
              <Tooltip title="إظهار/إخفاء الشبكة">
                <Button
                  size="small"
                  variant={showGrid ? "contained" : "outlined"}
                  onClick={() => setShowGrid(!showGrid)}
                >
                  الشبكة
                </Button>
              </Tooltip>
              <Tooltip title="تفعيل/تعطيل التقريب إلى الشبكة">
                <Button
                  size="small"
                  variant={snapToGrid ? "contained" : "outlined"}
                  onClick={() => setSnapToGrid(!snapToGrid)}
                >
                  التقريب للشبكة
                </Button>
              </Tooltip>
              <Tooltip title="حجم الشبكة">
                <Select
                  size="small"
                  value={gridSize}
                  onChange={(e) => setGridSize(Number(e.target.value))}
                  sx={{ width: 80 }}
                >
                  <MenuItem value={5}>5px</MenuItem>
                  <MenuItem value={10}>10px</MenuItem>
                  <MenuItem value={20}>20px</MenuItem>
                  <MenuItem value={50}>50px</MenuItem>
                </Select>
              </Tooltip>
              <Tooltip title="إظهار/إخفاء خطوط الإرشاد">
                <Button
                  size="small"
                  variant={showGuides ? "contained" : "outlined"}
                  onClick={() => setShowGuides(!showGuides)}
                >
                  خطوط الإرشاد
                </Button>
              </Tooltip>
              
              {/* تحكم في معامل التحويل */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="caption" sx={{ minWidth: 'max-content' }}>
                  معامل التحويل:
                </Typography>
                <TextField
                  size="small"
                  type="number"
                  value={mmToPxRatioInput}
                  onChange={(e) => {
                    const value = e.target.value;
                    setMmToPxRatioInput(value);
                    
                    // تحديث القيمة الرقمية للحسابات
                    const numericValue = value === '' ? 1 : (parseFloat(value) || 1);
                    if (numericValue >= 1 && numericValue <= 10) {
                      setMmToPxRatio(numericValue);
                    }
                  }}
                  inputProps={{
                    min: 1,
                    max: 10,
                    step: 0.1,
                    style: { width: '60px', textAlign: 'center' }
                  }}
                  sx={{ width: '80px' }}
                />
                <Typography variant="caption" color="text.secondary">
                  px/mm
                </Typography>
                
                {/* أزرار سريعة للمعاملات الشائعة */}
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Button
                    size="small"
                    variant={mmToPxRatio === 2.83 ? "contained" : "outlined"}
                    onClick={() => {
                      setMmToPxRatio(2.83);
                      setMmToPxRatioInput('2.83');
                    }}
                    sx={{ minWidth: '45px', fontSize: '0.7rem' }}
                  >
                    72 DPI
                  </Button>
                  <Button
                    size="small"
                    variant={mmToPxRatio === 3.78 ? "contained" : "outlined"}
                    onClick={() => {
                      setMmToPxRatio(3.78);
                      setMmToPxRatioInput('3.78');
                    }}
                    sx={{ minWidth: '45px', fontSize: '0.7rem' }}
                  >
                    96 DPI
                  </Button>
                  <Button
                    size="small"
                    variant={mmToPxRatio === 4.17 ? "contained" : "outlined"}
                    onClick={() => {
                      setMmToPxRatio(4.17);
                      setMmToPxRatioInput('4.17');
                    }}
                    sx={{ minWidth: '50px', fontSize: '0.7rem' }}
                  >
                    تجريبي
                  </Button>
                </Box>
              </Box>
            </Box>
            
            <Box
              ref={designAreaRef}
              id="template-design-area"
              onClick={(e) => {
                // Only clear selection if clicking directly on the design area (not on an element)
                if (e.currentTarget === e.target) {
                  setSelectedElementId(null);
                }
              }}
              // منع أي سلوك للسحب الافتراضي
              onDragOver={(e) => e.preventDefault()}
              onDrag={(e) => e.preventDefault()}
              onDragStart={(e) => e.preventDefault()}
              sx={{
                // استخدام معامل التحويل القابل للتعديل
                width: template.orientation === 'landscape' ?
                       (template.paperSize === 'A4' ? `${297 * mmToPxRatio}px` :
                       template.paperSize === 'A5' ? `${210 * mmToPxRatio}px` :
                       template.paperSize === 'A6' ? `${148 * mmToPxRatio}px` :
                       template.paperSize === 'thermal80mm' ? `${80 * mmToPxRatio}px` :
                       template.paperSize === 'thermal58mm' ? `${58 * mmToPxRatio}px` :
                       `${(template.customHeight || 297) * mmToPxRatio}px`) :
                       // أبعاد عمودية عادية
                       (template.paperSize === 'A4' ? `${210 * mmToPxRatio}px` :
                       template.paperSize === 'A5' ? `${148 * mmToPxRatio}px` :
                       template.paperSize === 'A6' ? `${105 * mmToPxRatio}px` :
                       template.paperSize === 'thermal80mm' ? `${80 * mmToPxRatio}px` :
                       template.paperSize === 'thermal58mm' ? `${58 * mmToPxRatio}px` :
                       `${(template.customWidth || 210) * mmToPxRatio}px`),
                height: template.orientation === 'landscape' ?
                        (template.paperSize === 'A4' ? `${210 * mmToPxRatio}px` :
                        template.paperSize === 'A5' ? `${148 * mmToPxRatio}px` :
                        template.paperSize === 'A6' ? `${105 * mmToPxRatio}px` :
                        template.paperSize === 'thermal80mm' ? `${297 * mmToPxRatio}px` :
                        template.paperSize === 'thermal58mm' ? `${297 * mmToPxRatio}px` :
                        `${(template.customWidth || 210) * mmToPxRatio}px`) :
                        // أبعاد عمودية عادية
                        (template.paperSize === 'A4' ? `${297 * mmToPxRatio}px` :
                        template.paperSize === 'A5' ? `${210 * mmToPxRatio}px` :
                        template.paperSize === 'A6' ? `${148 * mmToPxRatio}px` :
                        template.paperSize === 'thermal80mm' ? `${297 * mmToPxRatio}px` :
                        template.paperSize === 'thermal58mm' ? `${297 * mmToPxRatio}px` :
                        `${(template.customHeight || 297) * mmToPxRatio}px`),
                position: 'relative',
                backgroundColor: '#fff',
                boxShadow: '0 0 10px rgba(0,0,0,0.1)',
                overflow: 'visible', // السماح للعناصر بالظهور خارج الحدود
                userSelect: 'none',
                margin: '20px auto',
                padding: 0,
                // إضافة حدود لتوضيح حدود الورق الفعلية
                border: '2px solid #1976d2',
                borderRadius: '4px',
                boxSizing: 'border-box',
                // ضمان أن منطقة التصميم تأخذ المساحة الكاملة المطلوبة
                minHeight: template.orientation === 'landscape' ?
                          (template.paperSize === 'A4' ? `${210 * mmToPxRatio}px` :
                          template.paperSize === 'A5' ? `${148 * mmToPxRatio}px` :
                          template.paperSize === 'A6' ? `${105 * mmToPxRatio}px` :
                          template.paperSize === 'thermal80mm' ? `${297 * mmToPxRatio}px` :
                          template.paperSize === 'thermal58mm' ? `${297 * mmToPxRatio}px` :
                          `${(template.customWidth || 210) * mmToPxRatio}px`) :
                          (template.paperSize === 'A4' ? `${297 * mmToPxRatio}px` :
                          template.paperSize === 'A5' ? `${210 * mmToPxRatio}px` :
                          template.paperSize === 'A6' ? `${148 * mmToPxRatio}px` :
                          template.paperSize === 'thermal80mm' ? `${297 * mmToPxRatio}px` :
                          template.paperSize === 'thermal58mm' ? `${297 * mmToPxRatio}px` :
                          `${(template.customHeight || 297) * mmToPxRatio}px`),
                backgroundImage: showGrid ?
                  `
                    linear-gradient(to right, rgba(0, 0, 0, 0.05) 1px, transparent 1px),
                    linear-gradient(to bottom, rgba(0, 0, 0, 0.05) 1px, transparent 1px)
                  ` : 'none',
                backgroundSize: showGrid ? `${gridSize}px ${gridSize}px` : '0 0',
                // منع المتصفح من تداخل السلوكيات الافتراضية
                touchAction: 'none',
                WebkitUserDrag: 'none',
                MozUserSelect: 'none',
                WebkitUserSelect: 'none',
                msUserSelect: 'none'
              }}
            >
              {/* خطوط الإرشاد للمحاذاة */}
              {showGuides && activeGuides.map((guide, index) => (
                <Box
                  key={`guide-${index}`}
                  sx={{
                    position: 'absolute',
                    backgroundColor: '#2196f3',
                    zIndex: 9999,
                    ...(guide.type === 'horizontal' ? {
                      width: '100%',
                      height: '1px',
                      top: `${guide.position}px`,
                      left: 0
                    } : {
                      width: '1px',
                      height: '100%',
                      left: `${guide.position}px`,
                      top: 0
                    })
                  }}
                />
              ))}
              
              {/* عناصر القالب */}
              {template.elements && template.elements.map((element) => (
              <Box
                id={`element-${element.id}`}
                key={element.id}
                onClick={(e) => handleElementClick(element.id, e)}
                onMouseDown={(e) => handleDragStart(element.id, e)}
                draggable={false}
                data-element-id={element.id}
                sx={{
                  position: 'absolute',
                  top: `${element.position.y}px`,
                  left: `${element.position.x}px`,
                  zIndex: draggedElementId === element.id ? 1000 : element.position.zIndex,
                  width: `${element.style.width}px`,
                  height: `${element.style.height}px`,
                  padding: `${element.style.padding}px`,
                  color: element.style.color,
                  backgroundColor: element.style.backgroundColor,
                  touchAction: 'none', // منع إجراءات اللمس الافتراضية
                  transform: 'none', // منع أي تحويلات قد تؤثر على السحب
                  transformOrigin: '0 0', // تعيين نقطة أصل التحويل
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  MozUserSelect: 'none',
                  msUserSelect: 'none',
                  border: element.style.borderWidth
                    ? `${element.style.borderWidth}px ${element.style.borderStyle} ${element.style.borderColor}`
                    : 'none',
                  textAlign: element.style.textAlign,
                  fontSize: `${element.style.fontSize}px`,
                  fontFamily: element.style.fontFamily,
                  fontWeight: element.style.fontWeight,
                  cursor: draggedElementId === element.id ? 'grabbing' : 'move', // استخدام مؤشر الحركة بدلاً من القبضة
                  pointerEvents: 'auto',
                  WebkitUserDrag: 'none',
                  transition: 'none', // إزالة جميع الانتقالات لتحسين أداء السحب
                  boxShadow: draggedElementId === element.id
                    ? '0 5px 15px rgba(0,0,0,0.5)'
                    : 'none',
                  outline: selectedElementId === element.id
                    ? '2px solid #1976d2'
                    : 'none',
                  '&:hover': {
                    outline: selectedElementId !== element.id
                      ? '1px dashed #1976d2'
                      : '2px solid #1976d2',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                  },
                  WebkitTouchCallout: 'none',
                  WebkitTapHighlightColor: 'rgba(0,0,0,0)'
                }}
                >
                  {/* مقابض تغيير الحجم - تظهر فقط عند تحديد العنصر */}
                  {selectedElementId === element.id && (
                    <>
                      {/* مقبض الزاوية اليمنى العليا */}
                      <Box
                        onMouseDown={(e) => handleResizeStart(element.id, 'ne', e)}
                        sx={{
                          position: 'absolute',
                          width: '10px',
                          height: '10px',
                          backgroundColor: '#1976d2',
                          right: '-5px',
                          top: '-5px',
                          cursor: 'ne-resize',
                          borderRadius: '50%',
                          zIndex: 2,
                          boxShadow: '0 0 3px rgba(0,0,0,0.3)',
                          '&:hover': {
                            transform: 'scale(1.2)',
                            backgroundColor: '#2196f3'
                          }
                        }}
                        onMouseOver={() => setResizeHover('ne')}
                        onMouseOut={() => setResizeHover(null)}
                      />
                      
                      {/* مقبض الزاوية اليمنى السفلية */}
                      <Box
                        onMouseDown={(e) => handleResizeStart(element.id, 'se', e)}
                        sx={{
                          position: 'absolute',
                          width: '10px',
                          height: '10px',
                          backgroundColor: '#1976d2',
                          right: '-5px',
                          bottom: '-5px',
                          cursor: 'se-resize',
                          borderRadius: '50%',
                          zIndex: 2,
                          boxShadow: '0 0 3px rgba(0,0,0,0.3)',
                          '&:hover': {
                            transform: 'scale(1.2)',
                            backgroundColor: '#2196f3'
                          }
                        }}
                        onMouseOver={() => setResizeHover('se')}
                        onMouseOut={() => setResizeHover(null)}
                      />
                      
                      {/* مقبض الزاوية اليسرى العليا */}
                      <Box
                        onMouseDown={(e) => handleResizeStart(element.id, 'nw', e)}
                        sx={{
                          position: 'absolute',
                          width: '10px',
                          height: '10px',
                          backgroundColor: '#1976d2',
                          left: '-5px',
                          top: '-5px',
                          cursor: 'nw-resize',
                          borderRadius: '50%',
                          zIndex: 2,
                          boxShadow: '0 0 3px rgba(0,0,0,0.3)',
                          '&:hover': {
                            transform: 'scale(1.2)',
                            backgroundColor: '#2196f3'
                          }
                        }}
                        onMouseOver={() => setResizeHover('nw')}
                        onMouseOut={() => setResizeHover(null)}
                      />
                      
                      {/* مقبض الزاوية اليسرى السفلية */}
                      <Box
                        onMouseDown={(e) => handleResizeStart(element.id, 'sw', e)}
                        sx={{
                          position: 'absolute',
                          width: '10px',
                          height: '10px',
                          backgroundColor: '#1976d2',
                          left: '-5px',
                          bottom: '-5px',
                          cursor: 'sw-resize',
                          borderRadius: '50%',
                          zIndex: 2,
                          boxShadow: '0 0 3px rgba(0,0,0,0.3)',
                          '&:hover': {
                            transform: 'scale(1.2)',
                            backgroundColor: '#2196f3'
                          }
                        }}
                        onMouseOver={() => setResizeHover('sw')}
                        onMouseOut={() => setResizeHover(null)}
                      />
                      
                      {/* مقبض الجانب الأيمن */}
                      <Box
                        onMouseDown={(e) => handleResizeStart(element.id, 'e', e)}
                        sx={{
                          position: 'absolute',
                          width: '10px',
                          height: '100%',
                          right: '-5px',
                          top: '0',
                          cursor: 'e-resize',
                          zIndex: 2
                        }}
                      />
                      
                      {/* مقبض الجانب الأيسر */}
                      <Box
                        onMouseDown={(e) => handleResizeStart(element.id, 'w', e)}
                        sx={{
                          position: 'absolute',
                          width: '10px',
                          height: '100%',
                          left: '-5px',
                          top: '0',
                          cursor: 'w-resize',
                          zIndex: 2
                        }}
                      />
                      
                      {/* مقبض الجانب العلوي */}
                      <Box
                        onMouseDown={(e) => handleResizeStart(element.id, 'n', e)}
                        sx={{
                          position: 'absolute',
                          width: '100%',
                          height: '10px',
                          left: '0',
                          top: '-5px',
                          cursor: 'n-resize',
                          zIndex: 2
                        }}
                      />
                      
                      {/* مقبض الجانب السفلي */}
                      <Box
                        onMouseDown={(e) => handleResizeStart(element.id, 's', e)}
                        sx={{
                          position: 'absolute',
                          width: '100%',
                          height: '10px',
                          left: '0',
                          bottom: '-5px',
                          cursor: 's-resize',
                          zIndex: 2
                        }}
                      />
                    </>
                  )}
                  
                  {element.elementType === 'text' && element.content}
                  {element.elementType === 'field' && (element.showFieldName
                    ? `${element.content || availableFields.find(f => f.field === element.sourceField)?.label || element.sourceField}: [${element.sourceField || 'غير محدد'}]`
                    : `[${element.sourceField || 'غير محدد'}]`)}
                  {element.elementType === 'image' && element.content ?
                    <Box component="img" src={element.content} alt="صورة" sx={{ maxWidth: '100%', maxHeight: '100%' }} /> :
                    '(صورة)'
                  }
                  {element.elementType === 'barcode' && '(باركود)'}
                  {element.elementType === 'table' && (
                    <Box sx={{ overflow: 'auto', width: '100%', height: '100%', fontSize: 'smaller' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        {element.tableConfig?.showHeaders && (
                          <thead>
                            <tr>
                              {element.tableConfig.columns
                               .filter(col => col.visible !== false)
                               .map((col, index) => (
                                <th key={index} style={{
                                  border: '1px solid #ddd',
                                  padding: '2px',
                                  backgroundColor: element.tableConfig?.styles.headerBackground,
                                  color: element.tableConfig?.styles.headerTextColor,
                                  fontSize: `${element.tableConfig?.headerFontSize || element.tableConfig?.fontSize || 10}px`
                                }}>
                                  {col.label}
                                </th>
                              ))}
                            </tr>
                          </thead>
                        )}
                        <tbody>
                          {[1, 2].map((row, rowIdx) => (
                            <tr key={rowIdx} style={{
                              backgroundColor: rowIdx % 2 === 0
                                ? element.tableConfig?.styles.rowBackground
                                : element.tableConfig?.styles.alternateRowBackground
                            }}>
                              {element.tableConfig?.columns
                               .filter(col => col.visible !== false)
                               .map((col, colIdx) => (
                                <td key={colIdx} style={{
                                  border: '1px solid #ddd',
                                  padding: '2px',
                                  color: element.tableConfig?.styles.rowTextColor
                                }}>
                                  {`[${col.field}]`}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Box>
                  )}
                  {element.elementType === 'shape' && (
                    element.content === 'circle' ?
                      <Box sx={{
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        backgroundColor: element.style.backgroundColor
                      }} /> :
                      element.content === 'line' ?
                        <Box sx={{
                          width: '100%',
                          height: '2px',
                          backgroundColor: element.style.borderColor,
                          position: 'absolute',
                          top: '50%',
                          transform: 'translateY(-50%)'
                        }} /> :
                        null
                  )}
                </Box>
              ))}
              
              {/* رسالة ملاحظة عند عدم وجود عناصر */}
              {(!template.elements || template.elements.length === 0) && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center',
                    color: '#888',
                    width: '80%'
                  }}
                >
                  <Typography variant="body1">
                    لا توجد عناصر في هذا القالب. استخدم الأدوات على اليمين لإضافة عناصر جديدة.
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
      
      {/* لوحة خصائص العنصر المحدد */}
      {selectedElementId && (
        <Paper elevation={2} sx={{ mt: 2, p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6">خصائص العنصر</Typography>
                <Button
                  color="error"
                  size="small"
                  variant="outlined"
                  onClick={handleDeleteElement}
                >
                  حذف العنصر
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            {getSelectedElement() && (
              <ElementPropertiesEditor
                element={getSelectedElement()!}
                onUpdate={handleUpdateSelectedElement}
                availableFields={availableFields}
                onAddSelectedField={handleAddSelectedField}
                onRemoveSelectedField={handleRemoveSelectedFieldByName}
              />
            )}
          </Grid>
        </Paper>
      )}
      
      <Box mt={2}>
        <Alert severity="info">
          محرر قوالب الطباعة جاهز للاستخدام. يمكنك إضافة وتحرير العناصر المختلفة في القالب.
          <Typography variant="caption" display="block" mt={1}>
            <strong>تلميحات:</strong>
            <ul>
              <li>اسحب العناصر لتغيير مواضعها باستخدام الماوس</li>
              <li>استخدم مفاتيح الأسهم لتحريك العناصر بدقة</li>
              <li>اضغط Shift + الأسهم لتحريك العناصر بمقدار 10 بكسل</li>
              <li>اضغط Alt + الأسهم لتغيير حجم العناصر</li>
            </ul>
          </Typography>
        </Alert>
      </Box>
    </Box>
  );
};

// مكون منتقي الحقول
interface FieldSelectorProps {
  value: string;
  onChange: (field: string) => void;
  availableFields: IAvailableFieldOption[];
  placeholder?: string;
}

const FieldSelector: React.FC<FieldSelectorProps> = ({
  value,
  onChange,
  availableFields,
  placeholder = 'اختر حقلاً'
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<string[]>(Object.values(fieldGroups));
  
  // فلترة الحقول حسب مصطلح البحث
  const filteredFields = useMemo(() => {
    if (!searchTerm) return availableFields;
    
    return availableFields.filter(field =>
      field.label.includes(searchTerm) ||
      field.field.includes(searchTerm) ||
      (field.description && field.description.includes(searchTerm))
    );
  }, [availableFields, searchTerm]);
  
  // تجميع الحقول حسب المجموعات
  const groupedFields: Record<string, IAvailableFieldOption[]> = useMemo(() => {
    const groups: Record<string, IAvailableFieldOption[]> = {};
    
    for (const field of filteredFields) {
      if (!groups[field.group]) {
        groups[field.group] = [];
      }
      groups[field.group].push(field);
    }
    
    return groups;
  }, [filteredFields]);
  
  // التعامل مع توسيع/طي المجموعات
  const toggleGroup = (group: string) => {
    setExpandedGroups(prev =>
      prev.includes(group)
        ? prev.filter(g => g !== group)
        : [...prev, group]
    );
  };
  
  // التعامل مع تغيير اختيار الحقل
  const handleFieldChange = (fieldName: string) => {
    onChange(fieldName);
    setSearchTerm('');  // إعادة تعيين مصطلح البحث بعد الاختيار
  };
  
  return (
    <Box sx={{ width: '100%', direction: 'rtl' }}>
      {/* حقل البحث */}
      <TextField
        fullWidth
        size="small"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeholder="ابحث عن حقل..."
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 1 }}
      />
      
      {/* قائمة الحقول */}
      <Paper
        variant="outlined"
        sx={{
          maxHeight: 250,
          overflow: 'auto',
          border: '1px solid #ddd',
          borderRadius: 1
        }}
      >
        {Object.entries(groupedFields).map(([group, fields]) => (
          <Box key={group}>
            {/* عنوان المجموعة */}
            <Box
              sx={{
                p: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: '#f5f5f5',
                cursor: 'pointer',
                borderBottom: '1px solid #ddd',
                '&:hover': {
                  backgroundColor: '#eaeaea'
                }
              }}
              onClick={() => toggleGroup(group)}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                {group}
              </Typography>
              <IconButton size="small">
                {expandedGroups.includes(group) ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
            
            {/* حقول المجموعة */}
            <Collapse in={expandedGroups.includes(group)}>
              <List disablePadding>
                {fields.map((field: IAvailableFieldOption) => (
                  <ListItem
                    key={field.field}
                    button
                    dense
                    selected={value === field.field}
                    onClick={() => handleFieldChange(field.field)}
                    sx={{
                      pr: 2,
                      pl: 2,
                      py: 0.5,
                      borderBottom: '1px solid #f0f0f0',
                      backgroundColor: value === field.field ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                      }
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="body2" noWrap>
                          {field.label}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {field.field}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
                
                {fields.length === 0 && (
                  <ListItem dense sx={{ pl: 4 }}>
                    <ListItemText
                      primary={
                        <Typography variant="body2" color="text.secondary">
                          لا توجد حقول متطابقة
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
              </List>
            </Collapse>
          </Box>
        ))}
        
        {Object.keys(groupedFields).length === 0 && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              لا توجد حقول متطابقة مع البحث
            </Typography>
          </Box>
        )}
      </Paper>
      
      {/* عرض الحقل المختار */}
      {value && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            الحقل المختار:
          </Typography>
          <Box
            sx={{
              p: 1,
              border: '1px solid #1976d2',
              borderRadius: 1,
              backgroundColor: 'rgba(25, 118, 210, 0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Typography variant="body2">
              {availableFields.find(f => f.field === value)?.label || value}
            </Typography>
            <Chip
              size="small"
              label={value}
              variant="outlined"
              sx={{ fontSize: '0.7rem' }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

// مكون محرر خصائص العنصر المحدد
interface ElementPropertiesEditorProps {
  element: ITemplateElement;
  onUpdate: (updates: Partial<ITemplateElement>) => void;
  availableFields: IAvailableFieldOption[];
  onAddSelectedField: (fieldName: string) => void;
  onRemoveSelectedField: (fieldName: string) => void;
}

const ElementPropertiesEditor: React.FC<ElementPropertiesEditorProps> = ({
  element,
  onUpdate,
  availableFields,
  onAddSelectedField,
  onRemoveSelectedField
}) => {
  // إضافة حالة خاصة بإعدادات الجدول
  const [tableColumnsExpanded, setTableColumnsExpanded] = useState<boolean>(false);
  
  // إعدادات افتراضية للجدول
  const defaultTableConfig: ITableConfig = {
    showHeaders: true,
    columns: [
      { field: 'product', label: 'المنتج', width: 180, visible: true },
      { field: 'quantity', label: 'الكمية', width: 80, visible: true },
      { field: 'price', label: 'السعر', width: 80, visible: true },
      { field: 'total', label: 'الإجمالي', width: 90, visible: true }
    ],
    styles: {
      headerBackground: '#f0f0f0',
      headerTextColor: '#000000',
      rowBackground: '#ffffff',
      rowTextColor: '#000000',
      alternateRowBackground: '#f9f9f9'
    },
    populateFromInvoice: true,
    compactView: false,
    fontSize: 9,
    headerFontSize: 10, // مقاس خط رأس الجدول افتراضياً أكبر قليلاً من محتوى الجدول
    dynamicHeight: true, // تفعيل خاصية التمدد التلقائي بشكل افتراضي
    pushElementsDown: true // تفعيل خاصية إزاحة العناصر تلقائياً بشكل افتراضي
  };
  
  // الحصول على إعدادات الجدول (مع استخدام الإعدادات الافتراضية إذا لم تكن موجودة)
  const getTableConfig = () => {
    if (element.elementType === 'table') {
      const config = element.tableConfig || defaultTableConfig;
      // ضمان التوافق مع النظام السابق - إذا لم يكن هناك مقاس خط للرأس، استخدم مقاس الخط العام + 1
      if (!config.headerFontSize && config.fontSize) {
        config.headerFontSize = config.fontSize + 1;
      }
      return config;
    }
    return defaultTableConfig;
  };
  // تحديث محتوى النص
  const handleContentChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onUpdate({ content: e.target.value });
  };
  
  // تحديث حقل المصدر للحقول الديناميكية
  const handleSourceFieldChange = (e: React.ChangeEvent<{ value: unknown }>) => {
    const selectedField = e.target.value as string;
    const fieldOption = availableFields.find(f => f.field === selectedField);
    
    // الحصول على الحقل القديم قبل التحديث
    const oldField = element.sourceField;
    
    // إزالة الحقل القديم من قائمة الحقول المختارة إذا كان موجوداً
    if (oldField && oldField !== selectedField) {
      console.log('إزالة الحقل القديم:', oldField, 'من قائمة الحقول المختارة');
      onRemoveSelectedField(oldField);
    }
    
    onUpdate({
      sourceField: selectedField,
      content: fieldOption ? `[${fieldOption.label}]` : undefined
    });
    
    // إضافة الحقل الجديد إلى قائمة الحقول المختارة إذا لم يكن موجوداً
    if (selectedField) {
      onAddSelectedField(selectedField);
    }
  };
  
  // تحديث خصائص النمط
  const handleStyleChange = (property: keyof typeof element.style, value: any) => {
    onUpdate({
      style: {
        ...element.style,
        [property]: value
      }
    });
  };
  
  // تحديث خصائص الموقع
  const handlePositionChange = (property: keyof typeof element.position, value: number) => {
    onUpdate({
      position: {
        ...element.position,
        [property]: value
      }
    });
  };
  
  return (
    <>
      <Grid item xs={12} md={6}>
        <Typography variant="subtitle2" gutterBottom>المعلومات الأساسية</Typography>
        
        {element.elementType === 'text' && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>النص</Typography>
            <textarea
              value={element.content || ''}
              onChange={handleContentChange}
              style={{
                width: '100%',
                minHeight: '60px',
                padding: '8px',
                fontFamily: 'inherit',
                boxSizing: 'border-box'
              }}
            />
          </Box>
        )}
        
        {element.elementType === 'field' && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>اختر الحقل</Typography>
            <FieldSelector
              value={element.sourceField || ''}
              onChange={(fieldName) => {
                // تحديث الحقل المختار
                handleSourceFieldChange({
                  target: { value: fieldName }
                } as React.ChangeEvent<{ value: unknown }>);
                
                // إضافة الحقل إلى قائمة الحقول المختارة إذا لم يكن موجوداً
                onAddSelectedField(fieldName);
              }}
              availableFields={availableFields}
              placeholder="ابحث أو اختر حقلاً"
            />
            
            <Box sx={{ mt: 2, mb: 1 }}>
              <FormControl component="fieldset">
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    checked={Boolean(element?.showFieldName)}
                    onChange={(e) => onUpdate({
                      showFieldName: e.target.checked
                    })}
                    id="show-field-name"
                  />
                  <label htmlFor="show-field-name" style={{ marginRight: '8px' }}>
                    إظهار اسم الحقل في الطباعة
                  </label>
                </Box>
              </FormControl>
              <Box sx={{ mt: 0.5, fontSize: '0.8rem', color: 'text.secondary' }}>
                عند تفعيل هذا الخيار، سيظهر اسم الحقل متبوعًا بقيمته (مثال: رقم الفاتورة: 1001)
              </Box>
              
              {element?.showFieldName && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" gutterBottom>اسم الحقل المخصص</Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={element.content || ''}
                    onChange={(e) => onUpdate({
                      // استخدام حقل المحتوى لتخزين الاسم المخصص
                      content: e.target.value
                    })}
                    placeholder={availableFields.find(f => f.field === element.sourceField)?.label || ''}
                    helperText="اترك فارغًا لاستخدام الاسم الافتراضي للحقل"
                  />
                </Box>
              )}
            </Box>
            
            <Typography variant="body2" gutterBottom>الاسم المعروض</Typography>
            <Box sx={{ fontSize: '0.9rem', p: 1, border: '1px solid #ddd', borderRadius: '4px', bgcolor: '#f8f8f8' }}>
              {element.content || `[${element.sourceField || 'غير محدد'}]`}
            </Box>
          </Box>
        )}
        
        {element.elementType === 'image' && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>تحميل صورة من الجهاز</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = (loadEvent) => {
                      const base64String = loadEvent.target?.result as string;
                      onUpdate({ content: base64String });
                    };
                    reader.readAsDataURL(file);
                  }
                }}
                style={{
                  width: '100%',
                  padding: '8px',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box'
                }}
              />
              {element.content && (
                <Box sx={{ mt: 1, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    معاينة الصورة
                  </Typography>
                  <Box
                    component="img"
                    src={element.content}
                    alt="معاينة"
                    sx={{
                      maxWidth: '100%',
                      maxHeight: '150px',
                      border: '1px solid #ddd',
                      borderRadius: '4px'
                    }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        )}
        
        {element.elementType === 'barcode' && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>قيمة الباركود</Typography>
            <input
              type="text"
              value={element.content || ''}
              onChange={handleContentChange}
              style={{
                width: '100%',
                padding: '8px',
                fontFamily: 'inherit',
                boxSizing: 'border-box'
              }}
            />
          </Box>
        )}
        
        {element.elementType === 'shape' && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>نوع الشكل</Typography>
            <select
              value={element.content || 'rectangle'}
              onChange={(e) => onUpdate({ content: e.target.value })}
              style={{
                width: '100%',
                padding: '8px',
                fontFamily: 'inherit',
                boxSizing: 'border-box'
              }}
            >
              <option value="rectangle">مستطيل</option>
              <option value="circle">دائرة</option>
              <option value="line">خط</option>
            </select>
          </Box>
        )}
        
        <Typography variant="subtitle2" gutterBottom>الموقع والحجم</Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2" gutterBottom>الموقع X</Typography>
            <input
              type="number"
              value={element.position.x}
              onChange={(e) => handlePositionChange('x', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '8px',
                fontFamily: 'inherit',
                boxSizing: 'border-box'
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" gutterBottom>الموقع Y</Typography>
            <input
              type="number"
              value={element.position.y}
              onChange={(e) => handlePositionChange('y', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '8px',
                fontFamily: 'inherit',
                boxSizing: 'border-box'
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" gutterBottom>العرض</Typography>
            <input
              type="number"
              value={element.style.width}
              onChange={(e) => handleStyleChange('width', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '8px',
                fontFamily: 'inherit',
                boxSizing: 'border-box'
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" gutterBottom>الارتفاع</Typography>
            <input
              type="number"
              value={element.style.height}
              onChange={(e) => handleStyleChange('height', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '8px',
                fontFamily: 'inherit',
                boxSizing: 'border-box'
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" gutterBottom>الطبقة (Z-Index)</Typography>
            <input
              type="number"
              value={element.position.zIndex}
              onChange={(e) => handlePositionChange('zIndex', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '8px',
                fontFamily: 'inherit',
                boxSizing: 'border-box'
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" gutterBottom>المساحة الداخلية (Padding)</Typography>
            <input
              type="number"
              value={element.style.padding}
              onChange={(e) => handleStyleChange('padding', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '8px',
                fontFamily: 'inherit',
                boxSizing: 'border-box'
              }}
            />
          </Grid>
        </Grid>
      </Grid>
      
      {/* قسم خصائص الجدول - يظهر فقط عندما يكون العنصر من نوع جدول */}
      {element.elementType === 'table' && (
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                mb: 1
              }}
              onClick={() => setTableColumnsExpanded(!tableColumnsExpanded)}
            >
              <Typography variant="subtitle2">إعدادات الجدول</Typography>
              <IconButton size="small">
                {tableColumnsExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
            
            <Collapse in={tableColumnsExpanded}>
              <Box sx={{ mb: 2 }}>
                <FormControl component="fieldset" sx={{ width: '100%', mb: 2 }}>
                  <Typography variant="body2" gutterBottom>إظهار رؤوس الأعمدة</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={getTableConfig().showHeaders}
                      onChange={(e) => onUpdate({
                        tableConfig: {
                          ...getTableConfig(),
                          showHeaders: e.target.checked
                        }
                      })}
                      id="show-headers"
                    />
                    <label htmlFor="show-headers" style={{ marginRight: '8px' }}>
                      إظهار رؤوس الأعمدة
                    </label>
                  </Box>
                </FormControl>
                
                <FormControl component="fieldset" sx={{ width: '100%', mb: 2 }}>
                  <Typography variant="body2" gutterBottom>تعبئة من الفاتورة المحفوظة</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={getTableConfig().populateFromInvoice === true}
                      onChange={(e) => onUpdate({
                        tableConfig: {
                          ...getTableConfig(),
                          populateFromInvoice: e.target.checked
                        }
                      })}
                      id="populate-from-invoice"
                    />
                    <label htmlFor="populate-from-invoice" style={{ marginRight: '8px' }}>
                      تعبئة تلقائية من بيانات الفاتورة المحفوظة
                    </label>
                  </Box>
                </FormControl>

                <FormControl component="fieldset" sx={{ width: '100%', mb: 2 }}>
                  <Typography variant="body2" gutterBottom>وضع الجدول المضغوط</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={getTableConfig().compactView === true}
                      onChange={(e) => onUpdate({
                        tableConfig: {
                          ...getTableConfig(),
                          compactView: e.target.checked
                        }
                      })}
                      id="compact-table"
                    />
                    <label htmlFor="compact-table" style={{ marginRight: '8px' }}>
                      استخدام نمط الجدول المضغوط (خط أصغر ومساحات أقل)
                    </label>
                  </Box>
                </FormControl>

                <FormControl component="fieldset" sx={{ width: '100%', mb: 2 }}>
                  <Typography variant="body2" gutterBottom>الجدول الديناميكي</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={getTableConfig().dynamicHeight === true}
                      onChange={(e) => onUpdate({
                        tableConfig: {
                          ...getTableConfig(),
                          dynamicHeight: e.target.checked
                        }
                      })}
                      id="dynamic-table"
                    />
                    <label htmlFor="dynamic-table" style={{ marginRight: '8px' }}>
                      تمديد الجدول تلقائياً حسب عدد المنتجات
                    </label>
                  </Box>
                </FormControl>

                <FormControl component="fieldset" sx={{ width: '100%', mb: 2 }}>
                  <Typography variant="body2" gutterBottom>إزاحة العناصر</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={getTableConfig().pushElementsDown === true}
                      onChange={(e) => onUpdate({
                        tableConfig: {
                          ...getTableConfig(),
                          pushElementsDown: e.target.checked
                        }
                      })}
                      id="push-elements"
                    />
                    <label htmlFor="push-elements" style={{ marginRight: '8px' }}>
                      إزاحة العناصر تحت الجدول تلقائياً عند التمدد
                    </label>
                  </Box>
                </FormControl>

                <FormControl component="fieldset" sx={{ width: '100%', mb: 2 }}>
                  <Typography variant="body2" gutterBottom>حجم خط الجدول</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <input
                      type="number"
                      min="6"
                      max="14"
                      value={getTableConfig().fontSize || 9}
                      onChange={(e) => onUpdate({
                        tableConfig: {
                          ...getTableConfig(),
                          fontSize: Number(e.target.value)
                        }
                      })}
                      style={{ width: '60px', padding: '4px' }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      حجم الخط الافتراضي: 9
                    </Typography>
                  </Box>
                </FormControl>

                <FormControl component="fieldset" sx={{ width: '100%', mb: 2 }}>
                  <Typography variant="body2" gutterBottom>حجم خط رأس الجدول (الأعمدة)</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <input
                      type="number"
                      min="6"
                      max="16"
                      value={getTableConfig().headerFontSize || 10}
                      onChange={(e) => onUpdate({
                        tableConfig: {
                          ...getTableConfig(),
                          headerFontSize: Number(e.target.value)
                        }
                      })}
                      style={{ width: '60px', padding: '4px' }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      حجم خط الرأس الافتراضي: 10
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    يمكن أن يكون مقاس خط رأس الجدول مختلف عن بقية محتوى الجدول
                  </Typography>
                </FormControl>
                
                <Typography variant="body2" gutterBottom>الأعمدة المعروضة</Typography>
                <Box sx={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px', p: 1 }}>
                  {getTableConfig().columns.map((column, index) => (
                    <Box key={index} sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 1,
                      p: 1,
                      backgroundColor: column.visible !== false ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                      borderRadius: '4px'
                    }}>
                      <input
                        type="checkbox"
                        checked={column.visible !== false}
                        onChange={(e) => {
                          const tableConfig = getTableConfig();
                          const updatedColumns = [...tableConfig.columns];
                          updatedColumns[index] = {
                            ...updatedColumns[index],
                            visible: e.target.checked
                          };
                          onUpdate({
                            tableConfig: {
                              ...tableConfig,
                              columns: updatedColumns
                            }
                          });
                        }}
                        id={`col-visible-${index}`}
                      />
                      <label htmlFor={`col-visible-${index}`} style={{ marginRight: '8px', flex: 1 }}>
                        {column.label}
                      </label>
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1, mr: 1 }}>
                        ({column.field})
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <input
                          type="number"
                          value={column.width}
                          onChange={(e) => {
                            const tableConfig = getTableConfig();
                            const updatedColumns = [...tableConfig.columns];
                            updatedColumns[index] = {
                              ...updatedColumns[index],
                              width: Number(e.target.value)
                            };
                            onUpdate({
                              tableConfig: {
                                ...tableConfig,
                                columns: updatedColumns
                              }
                            });
                          }}
                          style={{ width: '60px', padding: '4px' }}
                        />
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            const tableConfig = getTableConfig();
                            const updatedColumns = [...tableConfig.columns];
                            updatedColumns.splice(index, 1);
                            onUpdate({
                              tableConfig: {
                                ...tableConfig,
                                columns: updatedColumns
                              }
                            });
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}
                </Box>
                <Box sx={{ mt: 2, mb: 2 }}>
                  <Typography variant="body2" gutterBottom>إضافة عمود جديد</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          const tableConfig = getTableConfig();
                          const updatedColumns = [...tableConfig.columns, {
                            field: 'productName',
                            label: 'اسم المنتج',
                            width: 150,
                            visible: true
                          }];
                          onUpdate({
                            tableConfig: {
                              ...tableConfig,
                              columns: updatedColumns
                            }
                          });
                        }}
                      >
                        اسم المنتج
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          const tableConfig = getTableConfig();
                          const updatedColumns = [...tableConfig.columns, {
                            field: 'productVariable',
                            label: 'متغير المنتج',
                            width: 150,
                            visible: true
                          }];
                          onUpdate({
                            tableConfig: {
                              ...tableConfig,
                              columns: updatedColumns
                            }
                          });
                        }}
                      >
                        متغير المنتج
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          const tableConfig = getTableConfig();
                          const updatedColumns = [...tableConfig.columns, {
                            field: 'quantity',
                            label: 'الكمية',
                            width: 80,
                            visible: true
                          }];
                          onUpdate({
                            tableConfig: {
                              ...tableConfig,
                              columns: updatedColumns
                            }
                          });
                        }}
                      >
                        الكمية
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          const tableConfig = getTableConfig();
                          const updatedColumns = [...tableConfig.columns, {
                            field: 'unitName',
                            label: 'الوحدة',
                            width: 80,
                            visible: true
                          }];
                          onUpdate({
                            tableConfig: {
                              ...tableConfig,
                              columns: updatedColumns
                            }
                          });
                        }}
                      >
                        الوحدة
                      </Button>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          const tableConfig = getTableConfig();
                          const updatedColumns = [...tableConfig.columns, {
                            field: 'price',
                            label: 'السعر',
                            width: 80,
                            visible: true
                          }];
                          onUpdate({
                            tableConfig: {
                              ...tableConfig,
                              columns: updatedColumns
                            }
                          });
                        }}
                      >
                        السعر
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          const tableConfig = getTableConfig();
                          const updatedColumns = [...tableConfig.columns, {
                            field: 'discount',
                            label: 'الخصم',
                            width: 80,
                            visible: true
                          }];
                          onUpdate({
                            tableConfig: {
                              ...tableConfig,
                              columns: updatedColumns
                            }
                          });
                        }}
                      >
                        الخصم
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          const tableConfig = getTableConfig();
                          const updatedColumns = [...tableConfig.columns, {
                            field: 'tax',
                            label: 'الضريبة',
                            width: 80,
                            visible: true
                          }];
                          onUpdate({
                            tableConfig: {
                              ...tableConfig,
                              columns: updatedColumns
                            }
                          });
                        }}
                      >
                        الضريبة
                      </Button>

                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          const tableConfig = getTableConfig();
                          const updatedColumns = [...tableConfig.columns, {
                            field: 'product',
                            label: 'المنتج (مجمع)',
                            width: 180,
                            visible: true
                          }];
                          onUpdate({
                            tableConfig: {
                              ...tableConfig,
                              columns: updatedColumns
                            }
                          });
                        }}
                      >
                        المنتج (مجمع)
                      </Button>

                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          const tableConfig = getTableConfig();
                          const updatedColumns = [...tableConfig.columns, {
                            field: 'priceWithTax',
                            label: 'السعر شامل الضريبة',
                            width: 80,
                            visible: true
                          }];
                          onUpdate({
                            tableConfig: {
                              ...tableConfig,
                              columns: updatedColumns
                            }
                          });
                        }}
                      >
                        السعر شامل الضريبة
                      </Button>

                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          const tableConfig = getTableConfig();
                          const updatedColumns = [...tableConfig.columns, {
                            field: 'priceBeforeTax',
                            label: 'قبل الضريبة',
                            width: 80,
                            visible: true
                          }];
                          onUpdate({
                            tableConfig: {
                              ...tableConfig,
                              columns: updatedColumns
                            }
                          });
                        }}
                      >
                        قبل الضريبة
                      </Button>

                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          const tableConfig = getTableConfig();
                          const updatedColumns = [...tableConfig.columns, {
                            field: 'taxPercentage',
                            label: 'الضريبة %',
                            width: 80,
                            visible: true
                          }];
                          onUpdate({
                            tableConfig: {
                              ...tableConfig,
                              columns: updatedColumns
                            }
                          });
                        }}
                      >
                        الضريبة %
                      </Button>

                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          const tableConfig = getTableConfig();
                          const updatedColumns = [...tableConfig.columns, {
                            field: 'taxValue',
                            label: 'قيمة الضريبة',
                            width: 80,
                            visible: true
                          }];
                          onUpdate({
                            tableConfig: {
                              ...tableConfig,
                              columns: updatedColumns
                            }
                          });
                        }}
                      >
                        قيمة الضريبة
                      </Button>

                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          const tableConfig = getTableConfig();
                          const updatedColumns = [...tableConfig.columns, {
                            field: 'total',
                            label: 'الإجمالي',
                            width: 90,
                            visible: true
                          }];
                          onUpdate({
                            tableConfig: {
                              ...tableConfig,
                              columns: updatedColumns
                            }
                          });
                        }}
                      >
                        الإجمالي
                      </Button>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        const tableConfig = getTableConfig();
                        const updatedColumns = [...tableConfig.columns, {
                          field: 'new_column',
                          label: 'عمود جديد',
                          width: 100,
                          visible: true
                        }];
                        onUpdate({
                          tableConfig: {
                            ...tableConfig,
                            columns: updatedColumns
                          }
                        });
                      }}
                    >
                      إضافة عمود مخصص
                    </Button>
                  </Box>
                </Box>
              </Box>
              
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" gutterBottom>أنماط الجدول</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption">لون خلفية الترويسة</Typography>
                    <input
                      type="color"
                      value={getTableConfig().styles.headerBackground}
                      onChange={(e) => onUpdate({
                        tableConfig: {
                          ...getTableConfig(),
                          styles: {
                            ...getTableConfig().styles,
                            headerBackground: e.target.value
                          }
                        }
                      })}
                      style={{ width: '100%', padding: '2px' }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption">لون نص الترويسة</Typography>
                    <input
                      type="color"
                      value={getTableConfig().styles.headerTextColor}
                      onChange={(e) => onUpdate({
                        tableConfig: {
                          ...getTableConfig(),
                          styles: {
                            ...getTableConfig().styles,
                            headerTextColor: e.target.value
                          }
                        }
                      })}
                      style={{ width: '100%', padding: '2px' }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption">لون خلفية الصف</Typography>
                    <input
                      type="color"
                      value={getTableConfig().styles.rowBackground}
                      onChange={(e) => onUpdate({
                        tableConfig: {
                          ...getTableConfig(),
                          styles: {
                            ...getTableConfig().styles,
                            rowBackground: e.target.value
                          }
                        }
                      })}
                      style={{ width: '100%', padding: '2px' }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption">لون خلفية الصف البديل</Typography>
                    <input
                      type="color"
                      value={getTableConfig().styles.alternateRowBackground}
                      onChange={(e) => onUpdate({
                        tableConfig: {
                          ...getTableConfig(),
                          styles: {
                            ...getTableConfig().styles,
                            alternateRowBackground: e.target.value
                          }
                        }
                      })}
                      style={{ width: '100%', padding: '2px' }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption">لون نص الصف</Typography>
                    <input
                      type="color"
                      value={getTableConfig().styles.rowTextColor}
                      onChange={(e) => onUpdate({
                        tableConfig: {
                          ...getTableConfig(),
                          styles: {
                            ...getTableConfig().styles,
                            rowTextColor: e.target.value
                          }
                        }
                      })}
                      style={{ width: '100%', padding: '2px' }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Collapse>
          </Paper>
        </Grid>
      )}
      
      <Grid item xs={12} md={6}>
        <Typography variant="subtitle2" gutterBottom>خصائص النص</Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2" gutterBottom>حجم الخط</Typography>
            <input
              type="number"
              value={element.style.fontSize}
              onChange={(e) => handleStyleChange('fontSize', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '8px',
                fontFamily: 'inherit',
                boxSizing: 'border-box'
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" gutterBottom>الخط</Typography>
            <select
              value={element.style.fontFamily}
              onChange={(e) => handleStyleChange('fontFamily', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                fontFamily: 'inherit',
                boxSizing: 'border-box'
              }}
            >
              <option value="Cairo">Cairo</option>
              <option value="DroidArabicNaskh">Droid Arabic Naskh</option>
              <option value="Arial">Arial</option>
              <option value="Tahoma">Tahoma</option>
              <option value="Times New Roman">Times New Roman</option>
              <option value="Courier New">Courier New</option>
            </select>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" gutterBottom>وزن الخط</Typography>
            <select
              value={element.style.fontWeight}
              onChange={(e) => handleStyleChange('fontWeight', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                fontFamily: 'inherit',
                boxSizing: 'border-box'
              }}
            >
              <option value="normal">عادي</option>
              <option value="bold">عريض</option>
              <option value="lighter">خفيف</option>
            </select>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" gutterBottom>محاذاة النص</Typography>
            <select
              value={element.style.textAlign}
              onChange={(e) => handleStyleChange('textAlign', e.target.value as TextAlign)}
              style={{
                width: '100%',
                padding: '8px',
                fontFamily: 'inherit',
                boxSizing: 'border-box'
              }}
            >
              <option value="right">يمين</option>
              <option value="left">يسار</option>
              <option value="center">وسط</option>
              <option value="justify">ضبط</option>
            </select>
          </Grid>
        </Grid>
        
        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>الألوان والحدود</Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2" gutterBottom>لون النص</Typography>
            <input
              type="color"
              value={element.style.color}
              onChange={(e) => handleStyleChange('color', e.target.value)}
              style={{
                width: '100%',
                padding: '2px',
                boxSizing: 'border-box'
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" gutterBottom>لون الخلفية</Typography>
            <input
              type="color"
              value={element.style.backgroundColor === 'transparent' ? '#ffffff' : element.style.backgroundColor}
              onChange={(e) => handleStyleChange('backgroundColor', e.target.value)}
              style={{
                width: '100%',
                padding: '2px',
                boxSizing: 'border-box'
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" gutterBottom>سمك الحدود</Typography>
            <input
              type="number"
              value={element.style.borderWidth}
              onChange={(e) => handleStyleChange('borderWidth', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '8px',
                fontFamily: 'inherit',
                boxSizing: 'border-box'
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" gutterBottom>نمط الحدود</Typography>
            <select
              value={element.style.borderStyle}
              onChange={(e) => handleStyleChange('borderStyle', e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                fontFamily: 'inherit',
                boxSizing: 'border-box'
              }}
            >
              <option value="none">بدون</option>
              <option value="solid">خط متصل</option>
              <option value="dashed">خط متقطع</option>
              <option value="dotted">خط منقط</option>
            </select>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" gutterBottom>لون الحدود</Typography>
            <input
              type="color"
              value={element.style.borderColor}
              onChange={(e) => handleStyleChange('borderColor', e.target.value)}
              style={{
                width: '100%',
                padding: '2px',
                boxSizing: 'border-box'
              }}
            />
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};


export default PrintTemplateEditorPage;